import { create } from 'zustand';
import {
  type ActionLabel,
  deleteActionLabel,
  fetchActionLabels,
  type SaveActionLabelPayload,
  saveActionLabel,
} from './actionLabels';
import { fetchJSON, HttpError } from './api';
import { hasAuthGroup, isLocalDevelopmentAuth } from './auth';
import {
  buildTasToleranceAuditLabels,
  decodeVoteToSet,
  encodeLabelsToBase64Url,
} from './bitEncoding';
import {
  apiPath,
  FLOW,
  getAdminGroupName,
  getAdminName,
  getAuditorGroupName,
  isAdminName,
  isResettingFlow,
  isTasFlow,
  resolveVideoUrl,
} from './constants';
import { setCookie } from './cookies';
import {
  applyDraftIfAvailable,
  clearDraftForOperator,
  consumeRestoredStartSecond,
  loadDraftForOperator,
  markDraftDirty,
  saveDraftSnapshot,
  setPendingRestoredStartSecond,
  startDraftAutosave,
  stopDraftAutosave,
} from './drafts';
import { isPreviewDurationMismatch } from './previewDurationCheck';
import { clearAccumulatedMs, loadAccumulatedMs } from './sessionTimer';
import {
  normalizeTaskQualityType,
  TASK_QUALITY_NORMAL,
  TASK_QUALITY_STAGED_ACCEPTABLE,
  TASK_QUALITY_STAGED_PASSIVE,
  TASK_QUALITY_STAGED_RESET,
  TASK_QUALITY_STAGED_UNACCEPTABLE,
} from './taskQuality';
import { showToast } from './toast';
import type {
  AnnotateNextResponse,
  AppState,
  AuthUser,
  CommitResponse,
  EpisodeDebugResponse,
  EpisodeSpecifyStatus,
  EpisodeStatsOperator,
  EpisodeStatsResponse,
  EpisodeSummary,
  EpisodeSummaryResponse,
  LlmState,
  LlmTask,
  MyStatsResponse,
  QueueAvailabilityResponse,
  SecshotVote,
  TaskQualityType,
  ViewerMode,
} from './types';
import { loadCachedVideo, storeCachedVideo } from './videoCache';
import { downloadVideoWithResume, type VideoChunkSnapshot } from './videoDownloader';
import {
  cleanupVideoPrefetcher,
  registerFrameExtractor,
  VideoFrameExtractor,
} from './videoFramePrefetcher';

// Constants
const BATCH_SIZE = 4;
const PREFETCH_BEFORE_BATCHES = 4;
const PREFETCH_AFTER_BATCHES = 24;
const PREFETCH_BEFORE_SECONDS = PREFETCH_BEFORE_BATCHES * BATCH_SIZE;
const PREFETCH_AFTER_SECONDS = PREFETCH_AFTER_BATCHES * BATCH_SIZE;
const requiresExplicitTaskQuality = FLOW === 'secshot' || FLOW === 'seccap';

// Viewer module-level vars
let frameExtractor: VideoFrameExtractor | null = null;
let totalPrefetchSeconds = 0;
const extractedSeconds = new Set<number>();
let prefetchTargetSeconds = 0;
let availablePrefetchSeconds = 0;
let latestPreviewBlob: Blob | null = null;
let latestPreviewCoveredSeconds = 0;
let latestPreviewLoadId = 0;
let activePreviewCoveredSeconds = 0;
let activePreviewLoadId = 0;
const activePreviewBlobUrls = new Set<string>();
let activeDownloadProgress = { text: 'Downloading video...', percent: 0 };
let nextEpisodeLookupId = 0;
let nextEpisodeLoadId = 0;
let activeEpisodeLoadId = 0;
let activeEpisodeAbortController: AbortController | null = null;
const PREVIEW_BLOB_REVOKE_DELAY_MS = 250;

function revokeBlobUrl(url: string): void {
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore stale or unsupported blob URLs
  }
}

function revokePreviewBlobUrl(url: string): void {
  globalThis.setTimeout(() => revokeBlobUrl(url), PREVIEW_BLOB_REVOKE_DELAY_MS);
}

function releasePreviewVideoUrl(url: string | null | undefined): void {
  if (!url || !activePreviewBlobUrls.delete(url)) return;
  revokePreviewBlobUrl(url);
}

function releaseAllPreviewVideoUrls(): void {
  activePreviewBlobUrls.forEach((url) => {
    revokePreviewBlobUrl(url);
  });
  activePreviewBlobUrls.clear();
}

function resetPreviewVideoState(): void {
  latestPreviewBlob = null;
  latestPreviewCoveredSeconds = 0;
  latestPreviewLoadId = 0;
  activePreviewCoveredSeconds = 0;
  activePreviewLoadId = 0;
  releaseAllPreviewVideoUrls();
}

function beginEpisodeLoad(): { id: number; controller: AbortController } {
  if (activeEpisodeAbortController && !activeEpisodeAbortController.signal.aborted) {
    activeEpisodeAbortController.abort();
  }
  const controller = new AbortController();
  nextEpisodeLoadId += 1;
  activeEpisodeLoadId = nextEpisodeLoadId;
  activeEpisodeAbortController = controller;
  return { id: activeEpisodeLoadId, controller };
}

function cancelActiveEpisodeLoad(): void {
  if (activeEpisodeAbortController && !activeEpisodeAbortController.signal.aborted) {
    activeEpisodeAbortController.abort();
  }
  activeEpisodeAbortController = null;
  nextEpisodeLoadId += 1;
  activeEpisodeLoadId = nextEpisodeLoadId;
}

function finishEpisodeLoad(loadId: number): void {
  if (activeEpisodeLoadId === loadId) {
    activeEpisodeAbortController = null;
  }
}

function isEpisodeLoadCurrent(loadId: number, signal: AbortSignal): boolean {
  return activeEpisodeLoadId === loadId && !signal.aborted;
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

function createAbortError(): Error {
  const error = new Error('Episode load superseded');
  error.name = 'AbortError';
  return error;
}

// Incremented on every openEpisode call so a stale concurrent load can detect it has been
// superseded before writing viewer state or registering frame callbacks.
let openEpisodeToken = 0;

function isOpenEpisodeCurrent(token: number): boolean {
  return openEpisodeToken === token;
}

// Helpers
function setHashId(id: string | null): void {
  if (typeof window === 'undefined') return;
  if (id) {
    window.location.hash = `#id/${id}`;
  } else if (window.location.hash.startsWith('#id/')) {
    window.location.hash = '';
  }
}

function readHashId(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  if (!hash.startsWith('#id/')) return null;
  const value = hash.slice(4);
  return value ? value : null;
}

function findVoteForOperator(
  votes: SecshotVote[] | undefined,
  operator: string
): SecshotVote | null {
  if (!votes || !operator) return null;
  return votes.find((vote) => vote.operator === operator) ?? null;
}

function normalizeEpisodeId(raw: string): string {
  return (raw || '').trim();
}

function episodeClaimErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'Episode could not be loaded.';
  if (typeof (error as { status?: unknown }).status !== 'number') return error.message;
  let reason = '';
  try {
    const payload = JSON.parse(error.message) as { reason?: unknown };
    reason = typeof payload.reason === 'string' ? payload.reason : '';
  } catch {
    return error.message;
  }
  const messages: Record<string, string> = {
    duration_repair_in_progress: 'Duration repair is already running. Please retry shortly.',
    preview_duration_unavailable:
      'Preview duration could not be read. The episode was moved to the repair quarantine.',
    model_vote_decode_failed:
      'The model prelabel is invalid. The episode was moved to the repair quarantine.',
    model_vote_too_short:
      'The model prelabel does not cover the full Preview. The episode was quarantined.',
    vote_policy_mismatch:
      'This episode already has production activity and cannot be repaired automatically.',
    operator_has_active_lease: 'You already have another active annotation task.',
    leased_by_other: 'Another operator is currently working on this episode.',
    vote_finalized: 'This annotation is already final and can no longer be edited.',
    episode_finalized: 'This episode is already final and can no longer be edited.',
    episode_released: 'This episode has been released and cannot be claimed.',
    episode_not_auditable: 'This episode is not awaiting audit.',
    insufficient_votes: 'This episode does not have enough finalized production votes.',
    self_audit_forbidden: 'You cannot audit an episode that contains your own annotation.',
    operator_has_active_audit_lease: 'You already have another active audit task.',
    active_lease: 'This episode already has an active lease.',
    repair_apply_failed: 'The episode changed during repair. Please retry.',
  };
  return messages[reason] ?? error.message;
}

function canOpenClaimFailureReadOnly(error: unknown): boolean {
  if (!(error instanceof HttpError)) return false;
  try {
    const payload = JSON.parse(error.message) as { reason?: unknown };
    return [
      'leased_by_other',
      'operator_has_active_lease',
      'vote_finalized',
      'episode_finalized',
    ].includes(typeof payload.reason === 'string' ? payload.reason : '');
  } catch {
    return false;
  }
}

function apiErrorReason(error: unknown): string | null {
  if (!(error instanceof HttpError)) return null;
  try {
    const payload = JSON.parse(error.message) as { reason?: unknown };
    return typeof payload.reason === 'string' ? payload.reason : null;
  } catch {
    return null;
  }
}
const RECENT_EPISODE_IDS_KEY = 'secshot_recent_episode_ids_v1';
const RECENT_EPISODE_IDS_LIMIT = 8;
const OPERATOR_AUTO_REFRESH_KEY = 'secshot_operator_auto_refresh_v1';
const ADMIN_AUTO_REFRESH_KEY = 'secshot_admin_auto_refresh_v1';

function loadRecentEpisodeIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_EPISODE_IDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item.length > 0);
  } catch {
    return [];
  }
}

function saveRecentEpisodeIds(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(RECENT_EPISODE_IDS_KEY, JSON.stringify(ids));
  } catch {
    // ignore storage failures
  }
}

function loadAutoRefresh(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return false;
    return raw === '1' || raw === 'true' || raw === 'yes';
  } catch {
    return false;
  }
}

function saveAutoRefresh(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // ignore storage failures
  }
}

function resolveAnnotatedImagesCount(
  data: EpisodeStatsResponse,
  operator: string,
  isAdmin: boolean
): number {
  if (operator && !isAdmin) {
    const match = data.operators.find((op) => op.name === operator);
    if (match) {
      return match.images;
    }
    return 0;
  }
  return data.total_annotated_images ?? 0;
}

function getSummaryCacheKey(page: number, pageSize: number, status: string): string {
  return `${page}:${pageSize}:${status}`;
}

function coerceDeviceTypeEnum(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function resolveDeviceTypeEnum(source: unknown): number | null {
  if (!source || typeof source !== 'object' || source === null) return null;
  const src = source as Record<string, unknown>;
  const direct = coerceDeviceTypeEnum(src.deviceTypeEnum);
  if (direct != null) return direct;
  return coerceDeviceTypeEnum(src.device_type_enum);
}

function coerceCameraPosition(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function resolveCameraPosition(source: unknown): string | null {
  if (!source || typeof source !== 'object' || source === null) return null;
  const src = source as Record<string, unknown>;
  return coerceCameraPosition(src.cameraPosition) ?? coerceCameraPosition(src.camera_position);
}

function createSecondsRange(start: number, end: number): number[] {
  const result: number[] = [];
  for (let second = Math.max(0, start); second < end; second += 1) {
    result.push(second);
  }
  return result;
}

function decodeVoteToLabels(
  totalSeconds: number,
  votes: SecshotVote[] | null | undefined,
  operator: string
): Record<string, boolean> {
  const labels: Record<string, boolean> = {};
  const total = Math.max(0, totalSeconds);
  const activeVote = (votes || []).find((v) => v?.operator === operator && v.encoded) || null;
  if (!activeVote) {
    for (let i = 0; i < total; i += 1) {
      labels[String(i)] = true;
    }
    return labels;
  }
  const limit = Math.max(0, total);
  const set = decodeVoteToSet(activeVote, limit);
  for (let idx = 0; idx < total; idx += 1) {
    const name = String(idx);
    if (!set || idx >= limit) {
      labels[name] = true;
      continue;
    }
    labels[name] = set.has(idx);
  }
  return labels;
}

function countUniqueVoteOperators(votes: SecshotVote[] | null | undefined): number {
  const seen = new Set<string>();
  (votes || []).forEach((vote) => {
    if (vote?.operator) seen.add(vote.operator);
  });
  return seen.size;
}

function resolveInitialTaskQuality(
  episode: AnnotateNextResponse['episode'] | EpisodeDebugResponse['episode'] | null | undefined,
  viewerMode: AppState['viewerMode'],
  operator: string
): { type: TaskQualityType | null; conflict: boolean } {
  // Fresh-annotation default. In the resetting flow the natural starting point
  // is "1-1: annotate" (STAGED_RESET). Secshot/seccap must be selected
  // explicitly so a missed choice cannot silently become "2: normal".
  const defaultType = isResettingFlow
    ? TASK_QUALITY_STAGED_RESET
    : requiresExplicitTaskQuality
      ? null
      : TASK_QUALITY_NORMAL;
  if (!episode) return { type: defaultType, conflict: false };

  if (viewerMode === 'reviewing') {
    const ownVote = findVoteForOperator(episode.secshot_votes ?? undefined, operator);
    return {
      type: normalizeTaskQualityType(ownVote?.task_quality_type),
      conflict: false,
    };
  }

  if (viewerMode === 'auditing') {
    if (episode.secshot_audit) {
      return {
        type: normalizeTaskQualityType(episode.secshot_audit.task_quality_type),
        conflict: false,
      };
    }

    const uniqueVotes = new Map<string, SecshotVote>();
    (episode.secshot_votes ?? []).forEach((vote) => {
      if (vote.operator && !uniqueVotes.has(vote.operator)) uniqueVotes.set(vote.operator, vote);
    });
    const qualityTypes = new Set(
      Array.from(uniqueVotes.values()).map((vote) =>
        normalizeTaskQualityType(vote.task_quality_type)
      )
    );
    if (qualityTypes.size === 1)
      return { type: Array.from(qualityTypes)[0] ?? null, conflict: false };
    if (qualityTypes.size > 1) return { type: defaultType, conflict: true };
  }

  return { type: defaultType, conflict: false };
}

export const storeTestUtils = {
  setHashId,
  readHashId,
  findVoteForOperator,
  resolveAnnotatedImagesCount,
  getSummaryCacheKey,
  coerceDeviceTypeEnum,
  resolveDeviceTypeEnum,
  coerceCameraPosition,
  resolveCameraPosition,
  decodeVoteToLabels,
  countUniqueVoteOperators,
  resolveInitialTaskQuality,
};

function resetPrefetchState(): void {
  totalPrefetchSeconds = 0;
  frameExtractor = null;
  registerFrameExtractor(null);
  extractedSeconds.clear();
  prefetchTargetSeconds = 0;
  availablePrefetchSeconds = 0;
  resetPreviewVideoState();
  activeDownloadProgress = { text: 'Downloading video...', percent: 0 };
}

type BalancedPrefetchResult = {
  blocked: boolean;
  visibleSeconds: number[];
};

function getMissingSeconds(start: number, end: number, imageUrls: string[]): number[] {
  return createSecondsRange(start, end).filter((second) => !imageUrls[second]);
}

function scheduleBalancedFramePrefetch(
  startSecond: number,
  imageUrls: string[]
): BalancedPrefetchResult {
  if (!frameExtractor || totalPrefetchSeconds <= 0) {
    return { blocked: false, visibleSeconds: [] };
  }

  const maxStart = Math.max(0, totalPrefetchSeconds - 1);
  const currentStart = Math.max(0, Math.min(maxStart, Math.floor(startSecond)));
  const visibleEnd = Math.min(totalPrefetchSeconds, currentStart + BATCH_SIZE);
  if (visibleEnd <= currentStart) {
    return { blocked: false, visibleSeconds: [] };
  }
  if (availablePrefetchSeconds < visibleEnd) {
    return { blocked: true, visibleSeconds: [] };
  }

  const prefetchStart = Math.max(0, currentStart - PREFETCH_BEFORE_SECONDS);
  const prefetchEnd = Math.min(
    totalPrefetchSeconds,
    availablePrefetchSeconds,
    currentStart + BATCH_SIZE + PREFETCH_AFTER_SECONDS
  );
  const retainSeconds = new Set(createSecondsRange(prefetchStart, prefetchEnd));
  const visibleSeconds = getMissingSeconds(currentStart, visibleEnd, imageUrls);
  const visibleSecondSet = new Set(visibleSeconds);
  const nearbySeconds = createSecondsRange(prefetchStart, prefetchEnd).filter(
    (second) => !visibleSecondSet.has(second) && !imageUrls[second]
  );

  frameExtractor.ensureSeconds(visibleSeconds, true, {
    replaceQueued: true,
    retainQueuedSeconds: retainSeconds,
  });
  if (nearbySeconds.length > 0) {
    frameExtractor.ensureSeconds(nearbySeconds);
  }
  prefetchTargetSeconds = Math.max(prefetchTargetSeconds, prefetchEnd);

  return { blocked: false, visibleSeconds };
}

function rememberLatestPreviewBlob(blob: Blob, coveredSeconds: number, loadId: number): void {
  latestPreviewBlob = blob;
  latestPreviewCoveredSeconds = Math.max(latestPreviewCoveredSeconds, coveredSeconds);
  latestPreviewLoadId = loadId;
}

function previewUrlCovers(
  url: string | null | undefined,
  requiredEndSecond: number,
  loadId: number
): boolean {
  return (
    !!url &&
    activePreviewBlobUrls.has(url) &&
    activePreviewLoadId === loadId &&
    activePreviewCoveredSeconds >= requiredEndSecond
  );
}

function createPreviewUrlFor(requiredEndSecond: number, loadId: number): string | null {
  if (latestPreviewLoadId !== loadId) return null;
  if (!latestPreviewBlob || latestPreviewCoveredSeconds < requiredEndSecond) return null;
  const url = URL.createObjectURL(latestPreviewBlob);
  activePreviewBlobUrls.add(url);
  activePreviewCoveredSeconds = latestPreviewCoveredSeconds;
  activePreviewLoadId = loadId;
  return url;
}

function createTransientVideoUrlFor(requiredEndSecond: number, loadId: number): string | null {
  if (latestPreviewLoadId !== loadId) return null;
  if (!latestPreviewBlob || latestPreviewCoveredSeconds < requiredEndSecond) return null;
  return URL.createObjectURL(latestPreviewBlob);
}

let urlCaptured = false;
let statsRequestInFlight: Promise<EpisodeStatsResponse> | null = null;
let queueAvailabilityRequestInFlight: Promise<QueueAvailabilityResponse | null> | null = null;
const myStatsRequestInFlight = new Map<string, Promise<MyStatsResponse | null>>();

// Stats Helpers
const STATS_CACHE_TTL_MS = 10 * 60 * 1000;
const QUEUE_AVAILABILITY_CACHE_TTL_MS = 60 * 1000;

function withStatsDefaults(data: EpisodeStatsResponse | null | undefined): EpisodeStatsResponse {
  const defaults = {
    total_pending_episodes: 0,
    device_filtered_pending_episodes: 0,
    claimable_pending_episodes: 0,
    total_annotated_episodes: 0,
    total_annotated_images: 0,
    total_voting_episodes: 0,
    total_completed_episodes: 0,
    total_claimable_audit_episodes: 0,
    total_audit_pending_confirmation_episodes: 0,
    total_audited_episodes: 0,
    total_audited_images: 0,
    operators: [],
    required_votes: 2,
  } satisfies EpisodeStatsResponse;

  if (!data) return defaults;

  return {
    ...defaults,
    ...data,
    operators: Array.isArray(data.operators) ? data.operators : defaults.operators,
  };
}

function isAuthUserResponse(value: AuthUser | null): value is AuthUser {
  if (!value || typeof value !== 'object') return false;
  if (!('user' in value) && !('groups' in value)) return false;

  const rawUser = value.user;
  const rawGroups = value.groups ?? null;

  const userIsValid = rawUser == null || typeof rawUser === 'string';
  const groupsAreValid =
    rawGroups == null ||
    (Array.isArray(rawGroups) && rawGroups.every((g) => typeof g === 'string'));

  return userIsValid && groupsAreValid;
}

function isStatsResponse(value: EpisodeStatsResponse | null): value is EpisodeStatsResponse {
  if (!value || typeof value !== 'object') return false;

  return (
    typeof value.total_pending_episodes === 'number' &&
    typeof value.total_annotated_episodes === 'number' &&
    typeof value.total_annotated_images === 'number' &&
    Array.isArray(value.operators)
  );
}

// Omit actions from initial state object
type AppStateData = Omit<
  AppState,
  | 'lookupEpisode'
  | 'showEpisodeSpecify'
  | 'addRecentEpisodeId'
  | 'resetEpisodeSpecifyState'
  | 'captureEpisodeIdFromUrl'
  | 'launchEpisodeSpecifyFromState'
  | 'showSection'
  | 'showComeBackLater'
  | 'fetchStatsData'
  | 'fetchQueueAvailability'
  | 'fetchMyStatsData'
  | 'fetchAuthUser'
  | 'updateDashboardMetrics'
  | 'refreshAnnotatedImages'
  | 'setOperatorAutoRefresh'
  | 'setAdminAutoRefresh'
  | 'setOperator'
  | 'fetchOperators'
  | 'registerOperator'
  | 'onOperatorSelected'
  | 'enterAdminMode'
  | 'loadEpisodeSummaries'
  | 'invalidateEpisodeSortCache'
  | 'updateCachedSummaryPage'
  | 'showEpisodeDebug'
  | 'showDashboard'
  | 'showUserSelection'
  | 'showAdminAnalytics'
  | 'showEpisodesManagement'
  | 'showOperatorDashboard'
  | 'loadOperatorHistory'
  | 'backToEpisodeList'
  | 'showViewerLoadingShell'
  | 'openEpisode'
  | 'commit'
  | 'reclaimCurrentEpisodeLease'
  | 'confirmOwnVote'
  | 'confirmOwnAudit'
  | 'toggle'
  | 'toggleRange'
  | 'toggleBatch'
  | 'nextBatch'
  | 'prevBatch'
  | 'startOperatorRandom'
  | 'startAuditRandom'
  | 'releaseActiveLease'
  | 'resumeDraftIfAvailable'
  | 'releaseEpisodeResources'
  | 'resetAnnotationSession'
  | 'showGoldenAdmin'
  | 'showDataProcessAdmin'
  | 'showQualityFeedback'
  | 'dismissVotingCompleteDialog'
  | 'acknowledgePreviewDurationMismatch'
  | 'shiftWindowBy'
  | 'toggleZenMode'
  | 'setTaskQualityType'
  | 'loadActionLabels'
  | 'triggerLlmForSegmentEnding'
  | 'saveActionLabelToStore'
  | 'deleteActionLabelFromStore'
>;

const initialState: AppStateData = {
  page: 1,
  pageSize: 500,
  total: 0,
  episodes: [],
  operatorDashboardEpisodes: [],
  selectedEpisode: null,
  selectedDeviceTypeEnum: null,
  selectedCameraPosition: null,
  images: [],
  imageUrls: [],
  labels: {},
  visited: new Set<number>(),
  conflictFrames: new Set<string>(),
  batchIndex: 0,
  batchOffset: 0,
  operator: '',
  authUser: null,
  isAdmin: false,
  isAuditor: false,
  continuous: false,
  isLoading: false,
  mode: 'dashboardOverview',
  viewerMode: null,
  episodeEntryIntent: 'operator-claim',
  viewerReadOnly: false,
  leaseRecoveryRequired: false,
  rejectionMessage: null,
  showVotingCompleteDialog: false,
  votingCompleteNoCommit: false,
  previewDurationMismatch: false,
  leaseId: null,
  sessionStartTime: null,
  previousSection: null,
  stats: {
    completedEpisodes: 0,
    annotatedImages: 0,
    pendingEpisodes: 0,
    votingEpisodes: 0,
    auditPendingEpisodes: 0,
    auditAwaitingConfirmEpisodes: 0,
    auditedEpisodes: 0,
  },
  annotatedImages: 0,
  imagesLoading: false,
  currentEpisodeStatus: null,
  currentEpisodePriority: 9,
  currentEpisodeOperatorCount: 0,
  currentEpisodeVotes: null,
  currentOwnVoteAmendUntilMs: null,
  currentOwnVoteFinalizedAtMs: null,
  currentOwnAuditAmendUntilMs: null,
  statsCache: null,
  statsCacheAt: null,
  queueAvailability: null,
  queueAvailabilityAt: null,
  queueAvailabilityError: false,
  myStatsCache: null,
  myStatsCacheAt: null,
  sortedEpisodesCache: null,
  episodesLoaded: false,
  lastLoadedPage: null,
  lastLoadedPageSize: null,
  summaryPageCache: new Map(),
  summaryFilterStatus: 'all',
  episodeSpecifyId: null,
  episodeSpecifyFromUrl: null,
  episodeSpecifyStatus: null,
  episodeSpecifyMeta: null,
  episodeSpecifyBusy: false,
  recentEpisodeIds: loadRecentEpisodeIds(),
  episodeDebugData: null,
  debugEpisodeId: null,
  operatorAutoRefresh: loadAutoRefresh(OPERATOR_AUTO_REFRESH_KEY),
  adminAutoRefresh: loadAutoRefresh(ADMIN_AUTO_REFRESH_KEY),
  loadingProgress: null,
  videoUnblockedSeconds: 0,
  previewVideoUrl: null,
  toast: { message: null, visible: false },
  isZenMode: false,
  isCommitting: false,
  currentEpisodeTaskQualityType: requiresExplicitTaskQuality ? null : TASK_QUALITY_NORMAL,
  currentEpisodeTaskQualityConflict: false,
  currentEpisodeAnnotationFps: 1,
  actionLabels: {},
  actionLabelsLlmState: {},
};

export const useStore = create<AppState>((set, get) => ({
  ...initialState,

  toggleZenMode: () => {
    set((state) => ({ isZenMode: !state.isZenMode }));
  },

  fetchAuthUser: async () => {
    try {
      const authUser = await fetchJSON<AuthUser>('/api/me');
      if (!isAuthUserResponse(authUser)) {
        throw new Error('Unexpected auth payload');
      }
      const isAuditor =
        isLocalDevelopmentAuth(authUser) || hasAuthGroup(authUser, getAuditorGroupName());
      set({ authUser, isAuditor });
    } catch (error) {
      console.error('Failed to load auth user', error);
      set({ authUser: null, isAuditor: false });
    }
  },

  showSection: (sectionName: string) => {
    const { mode } = get();
    // Leaving the viewer (annotate/audit) for any other section must stop the
    // in-flight preview load. showSection is the single choke point every
    // navigation path goes through (logo/home icon, command palette, dashboards,
    // etc.), so aborting here covers "通过点击 icon 回到主页或其他方法离开" uniformly.
    // Guard on `sectionName !== 'viewer'` so re-entering the viewer (a load that
    // is just starting) is never torn down.
    if (mode === 'viewer' && sectionName !== 'viewer') {
      get().releaseEpisodeResources();
    }
    if (mode !== sectionName) {
      set({ previousSection: mode });
    }
    if (typeof window !== 'undefined') {
      if (sectionName !== 'viewer' && sectionName !== 'episodeSpecify') {
        if (window.location.hash.startsWith('#id/')) {
          window.location.hash = '';
        }
      }
    }
    set({ mode: sectionName });
  },

  showComeBackLater: () => {
    get().showSection('comeBackLater');
  },

  showDashboard: async () => {
    const { isAdmin, operator } = get();
    if (!isAdmin) {
      if (operator) {
        get().showOperatorDashboard();
      } else {
        await get().showUserSelection();
      }
      return;
    }
    get().showSection('dashboardOverview');
  },

  showUserSelection: async () => {
    set({ continuous: false });
    get().showSection('userSelect');
    await get().fetchOperators();
  },

  showAdminAnalytics: () => {
    const { isAdmin } = get();
    if (!isAdmin) {
      get().showComeBackLater();
      return;
    }
    get().showSection('adminStats');
  },

  showEpisodesManagement: () => {
    const { isAdmin } = get();
    if (!isAdmin) {
      get().showComeBackLater();
      return;
    }
    get().showSection('episodeList');
    void get().loadEpisodeSummaries({ forceReload: true, resetPage: false });
  },

  showOperatorDashboard: () => {
    get().showSection('operatorDashboard');
  },

  showGoldenAdmin: () => {
    const { isAdmin } = get();
    if (!isAdmin) {
      get().showComeBackLater();
      return;
    }
    get().showSection('goldenAdmin');
  },

  showDataProcessAdmin: () => {
    const { isAdmin } = get();
    if (!isAdmin) {
      get().showComeBackLater();
      return;
    }
    get().showSection('dataProcessAdmin');
  },

  showQualityFeedback: () => {
    get().showSection('qualityFeedback');
  },

  loadOperatorHistory: async () => {
    const { operator } = get();
    if (!operator) return;

    try {
      set({ isLoading: true });
      // Fetch recent history (e.g. last 100 items)
      const params = new URLSearchParams({
        operator,
        page: '1',
        page_size: '100',
        include_pending: 'true',
        include_completed: 'true',
      });
      const resp = await fetchJSON<EpisodeSummaryResponse>(`${apiPath('/summary')}?${params}`);
      set({ operatorDashboardEpisodes: resp?.episodes ?? [] });
    } catch (err) {
      console.error('Failed to load operator history', err);
      showToast('Failed to load history');
    } finally {
      set({ isLoading: false });
    }
  },

  backToEpisodeList: async (forceReload = false) => {
    get().releaseEpisodeResources();
    set({ continuous: false });

    if (typeof window !== 'undefined' && window.location.hash.startsWith('#id/')) {
      window.location.hash = '';
    }

    const { previousSection, isAdmin } = get();

    if (previousSection === 'episodeSpecify') {
      get().showSection('episodeSpecify');
      set({ sessionStartTime: null });
      return;
    }

    if (
      previousSection === 'operatorDashboard' ||
      previousSection === 'operator-annotate' ||
      previousSection === 'audit'
    ) {
      get().showOperatorDashboard();
      set({ sessionStartTime: null });
      return;
    }

    if (isAdmin) {
      if (previousSection === 'admin-episodes' || previousSection === 'episodeList') {
        get().showSection('episodeList');
        await get().loadEpisodeSummaries({ resetPage: false, forceReload });
      } else {
        await get().showDashboard();
      }
    } else {
      get().showComeBackLater();
    }
    set({ sessionStartTime: null });
  },

  fetchStatsData: async (forceRefresh = false) => {
    const { statsCache, statsCacheAt } = get();
    const now = Date.now();
    if (!forceRefresh && statsCache && statsCacheAt) {
      const age = now - statsCacheAt;
      if (age >= 0 && age < STATS_CACHE_TTL_MS) {
        return statsCache;
      }
    }
    if (statsRequestInFlight) {
      return statsRequestInFlight;
    }

    statsRequestInFlight = (async () => {
      try {
        const data = await fetchJSON<EpisodeStatsResponse>(apiPath('/stats'));
        if (!isStatsResponse(data)) {
          throw new Error('Unexpected stats payload');
        }

        const normalized = withStatsDefaults(data);
        set({ statsCache: normalized, statsCacheAt: now });
        return normalized;
      } catch (error) {
        console.error('Failed to load stats', error);
        const currentCache = get().statsCache;
        if (forceRefresh || !currentCache) {
          showToast('Failed to load stats');
        }
        return withStatsDefaults(currentCache);
      } finally {
        statsRequestInFlight = null;
      }
    })();

    return statsRequestInFlight;
  },

  fetchQueueAvailability: async (forceRefresh = false) => {
    const { operator, queueAvailability, queueAvailabilityAt } = get();
    const activeOperator = operator.trim();
    if (!activeOperator) {
      set({ queueAvailability: null, queueAvailabilityAt: null, queueAvailabilityError: true });
      return null;
    }
    const now = Date.now();
    if (
      !forceRefresh &&
      queueAvailability &&
      queueAvailability.operator === activeOperator &&
      queueAvailabilityAt &&
      now - queueAvailabilityAt >= 0 &&
      now - queueAvailabilityAt < QUEUE_AVAILABILITY_CACHE_TTL_MS
    ) {
      return queueAvailability;
    }
    if (queueAvailabilityRequestInFlight) return queueAvailabilityRequestInFlight;

    queueAvailabilityRequestInFlight = (async () => {
      try {
        const query = new URLSearchParams({ operator: activeOperator });
        const data = await fetchJSON<QueueAvailabilityResponse>(
          `${apiPath('/queue-availability')}?${query}`
        );
        if (get().operator.trim() === activeOperator) {
          set({
            queueAvailability: data,
            queueAvailabilityAt: now,
            queueAvailabilityError: false,
          });
        }
        return data;
      } catch (error) {
        console.error('Failed to load queue availability', error);
        if (get().operator.trim() === activeOperator) {
          set({
            queueAvailability: null,
            queueAvailabilityAt: null,
            queueAvailabilityError: true,
          });
        }
        return null;
      } finally {
        queueAvailabilityRequestInFlight = null;
      }
    })();
    return queueAvailabilityRequestInFlight;
  },

  fetchMyStatsData: async (forceRefresh = false) => {
    const { operator, myStatsCache, myStatsCacheAt } = get();
    if (!operator) return null;
    const now = Date.now();
    if (!forceRefresh && myStatsCache && myStatsCache.operator === operator && myStatsCacheAt) {
      const age = now - myStatsCacheAt;
      if (age >= 0 && age < STATS_CACHE_TTL_MS) {
        return myStatsCache;
      }
    }
    const existing = myStatsRequestInFlight.get(operator);
    if (existing) {
      return existing;
    }

    const promise = (async () => {
      try {
        const qs = new URLSearchParams({ operator });
        const data = await fetchJSON<MyStatsResponse>(`${apiPath('/me/stats')}?${qs}`);
        if (get().operator === operator) {
          set({ myStatsCache: data, myStatsCacheAt: now });
        }
        return data;
      } catch (error) {
        console.error('Failed to load my stats', error);
        const currentCache = get().myStatsCache;
        if (forceRefresh || !currentCache) {
          showToast('Failed to load stats');
        }
        return currentCache;
      } finally {
        myStatsRequestInFlight.delete(operator);
      }
    })();
    myStatsRequestInFlight.set(operator, promise);
    return promise;
  },

  refreshAnnotatedImages: () => {
    const { statsCache, operator, isAdmin } = get();
    const data = withStatsDefaults(statsCache);
    const count = resolveAnnotatedImagesCount(data, operator, isAdmin);
    set({ annotatedImages: count });
  },

  setOperatorAutoRefresh: (value: boolean) => {
    set({ operatorAutoRefresh: value });
    saveAutoRefresh(OPERATOR_AUTO_REFRESH_KEY, value);
  },

  setAdminAutoRefresh: (value: boolean) => {
    set({ adminAutoRefresh: value });
    saveAutoRefresh(ADMIN_AUTO_REFRESH_KEY, value);
  },

  updateDashboardMetrics: async () => {
    const { isAdmin } = get();
    if (isAdmin) {
      const [data] = await Promise.all([
        get().fetchStatsData(),
        get().fetchMyStatsData(),
        get().fetchQueueAvailability(),
      ]);
      const { operator } = get();
      const newStats = {
        completedEpisodes: data.total_annotated_episodes,
        annotatedImages: data.total_annotated_images,
        pendingEpisodes: data.total_pending_episodes,
        votingEpisodes: data.total_voting_episodes,
        auditPendingEpisodes: data.total_claimable_audit_episodes ?? data.total_completed_episodes,
        auditAwaitingConfirmEpisodes: data.total_audit_pending_confirmation_episodes ?? 0,
        auditedEpisodes: data.total_audited_episodes,
      };
      const annotatedCount = resolveAnnotatedImagesCount(data, operator, true);
      set({ stats: newStats, annotatedImages: annotatedCount });
      return;
    }
    const mine = await get().fetchMyStatsData();
    if (mine && mine.operator === get().operator) {
      set({ annotatedImages: mine.images });
    }
  },

  setOperator: (name: string | null) => {
    const { authUser } = get();
    const trimmed = (name || '').trim();
    const isHardcodedAdmin = !!trimmed && isAdminName(trimmed);
    const isGroupAdmin =
      isLocalDevelopmentAuth(authUser) || hasAuthGroup(authUser, getAdminGroupName());
    const isAdmin = isHardcodedAdmin || isGroupAdmin;

    if (isAdmin) {
      set({ continuous: false });
      setCookie('admin_mode', '1');
    }
    set({
      operator: trimmed,
      isAdmin,
      queueAvailability: null,
      queueAvailabilityAt: null,
      queueAvailabilityError: false,
    });
  },

  fetchOperators: async () => {
    await get().fetchStatsData();
  },

  registerOperator: async (name: string) => {
    const { statsCache } = get();
    const operators = statsCache?.operators || [];
    if (operators.some((op) => op.name === name)) {
      return;
    }
    try {
      await fetchJSON(apiPath('/operator'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const newOp = { name, episodes: 0, images: 0 } satisfies EpisodeStatsOperator;
      const newCache = statsCache
        ? { ...statsCache, operators: [...(statsCache.operators || []), newOp] }
        : null;
      set({ statsCache: newCache });
    } catch {
      // ignore
    }
  },

  onOperatorSelected: async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    get().setOperator(trimmed);
    setCookie('operator', trimmed);
    const { isAdmin, authUser } = get();
    if (isAdmin) {
      get().showOperatorDashboard();
      await get().updateDashboardMetrics();
    } else {
      if (!authUser?.user) {
        await get().registerOperator(trimmed);
      }
      void get().updateDashboardMetrics();
      const handled = await get().launchEpisodeSpecifyFromState();
      if (handled) return;
      get().showOperatorDashboard();
    }
  },

  enterAdminMode: async () => {
    const adminName = getAdminName();
    if (!adminName) return;
    get().setOperator(adminName);
    setCookie('operator', adminName);
    setCookie('admin_mode', '1');
    get().showSection('dashboardOverview');
    await Promise.resolve();
  },

  invalidateEpisodeSortCache: () => {
    set({ sortedEpisodesCache: null });
  },

  updateCachedSummaryPage: () => {
    const { page, pageSize, summaryFilterStatus, total, episodes, summaryPageCache } = get();
    const cacheKey = getSummaryCacheKey(page, pageSize, summaryFilterStatus);
    const cached = summaryPageCache.get(cacheKey);
    if (!cached) return;

    const newCache = new Map(summaryPageCache);
    newCache.set(cacheKey, {
      ...cached,
      total,
      page,
      page_size: pageSize,
      episodes,
    });
    set({ summaryPageCache: newCache });
  },

  loadEpisodeSummaries: async (options = {}) => {
    const resetPage = typeof options.resetPage === 'boolean' ? options.resetPage : true;
    const forceReload = typeof options.forceReload === 'boolean' ? options.forceReload : false;
    const {
      mode,
      page,
      pageSize,
      summaryFilterStatus,
      episodesLoaded,
      lastLoadedPage,
      lastLoadedPageSize,
      summaryPageCache,
    } = get();

    if (mode === 'operator-annotate') return;

    let requestedPage = page;
    if (resetPage) {
      requestedPage = 1;
      set({ page: 1 });
    }

    const cacheKey = getSummaryCacheKey(requestedPage, pageSize, summaryFilterStatus);

    if (!forceReload) {
      const cached = summaryPageCache.get(cacheKey);
      if (cached) {
        set({
          total: cached.total ?? 0,
          page: cached.page ?? requestedPage,
          pageSize: cached.page_size ?? pageSize,
          episodes: cached.episodes ?? [],
          episodesLoaded: true,
          lastLoadedPage: cached.page ?? requestedPage,
          lastLoadedPageSize: cached.page_size ?? pageSize,
        });
        get().invalidateEpisodeSortCache();
        return;
      }
    }

    if (
      episodesLoaded &&
      !forceReload &&
      lastLoadedPage === requestedPage &&
      lastLoadedPageSize === pageSize
    ) {
      return;
    }

    set({ isLoading: true });

    try {
      const params = new URLSearchParams({
        page: String(requestedPage),
        page_size: String(pageSize),
        status: summaryFilterStatus,
      });
      const resp = await fetchJSON<EpisodeSummaryResponse>(`${apiPath('/summary')}?${params}`);

      const newTotal = resp?.total ?? 0;
      const newPage = resp?.page ?? requestedPage;
      const newPageSize = resp?.page_size ?? pageSize;
      const newEpisodes = resp?.episodes ?? [];

      const newSummaryCache = new Map(summaryPageCache);
      if (resp) {
        const currentKey = getSummaryCacheKey(newPage, newPageSize, summaryFilterStatus);
        newSummaryCache.set(currentKey, resp);
      }

      set({
        total: newTotal,
        page: newPage,
        pageSize: newPageSize,
        episodes: newEpisodes,
        episodesLoaded: true,
        lastLoadedPage: newPage,
        lastLoadedPageSize: newPageSize,
        summaryPageCache: newSummaryCache,
      });
      get().invalidateEpisodeSortCache();
    } catch (err) {
      console.error('Failed to load episode summaries', err);
      set({
        total: 0,
        page: requestedPage,
        episodes: [],
        episodesLoaded: false,
        lastLoadedPage: null,
        lastLoadedPageSize: null,
      });
      get().invalidateEpisodeSortCache();
      showToast('Failed to load episodes');
    } finally {
      set({ isLoading: false });
    }
  },

  showEpisodeSpecify: async (initialId, options = {}) => {
    set({ continuous: false });
    const intent = options.intent ?? (get().isAdmin ? 'admin-readonly' : 'operator-claim');
    set({ episodeEntryIntent: intent });

    const { episodeSpecifyId, episodeSpecifyFromUrl } = get();
    const idToUse = normalizeEpisodeId(
      initialId ?? episodeSpecifyId ?? episodeSpecifyFromUrl ?? ''
    );

    const wantsViewerShell = options.showViewerPlaceholder === true && !!idToUse;
    if (wantsViewerShell) {
      get().showViewerLoadingShell(idToUse);
    } else {
      get().showSection('episodeSpecify');
    }

    if (idToUse) {
      set({ episodeSpecifyId: idToUse });
      setHashId(idToUse);
    } else {
      set({
        episodeSpecifyStatus: { message: '', tone: 'info', visible: false },
        episodeSpecifyMeta: { queue: '—', history: '—', visible: false },
        episodeSpecifyBusy: false,
      });
    }

    if (idToUse && options.autoSubmit !== false) {
      await get().lookupEpisode(idToUse, {
        autoOpen: true,
        showViewerPlaceholder: options.showViewerPlaceholder === true,
        intent,
      });
    }
  },

  resetEpisodeSpecifyState: () => {
    set({ episodeSpecifyId: null, episodeSpecifyFromUrl: null });
    setHashId(null);
    set({
      episodeSpecifyStatus: { message: '', tone: 'info', visible: false },
      episodeSpecifyMeta: { queue: '—', history: '—', visible: false },
      episodeSpecifyBusy: false,
    });
  },

  addRecentEpisodeId: (rawId: string) => {
    const episodeId = normalizeEpisodeId(rawId);
    if (!episodeId) return;
    const current = get().recentEpisodeIds;
    const deduped = [episodeId, ...current.filter((id) => id !== episodeId)];
    const trimmed = deduped.slice(0, RECENT_EPISODE_IDS_LIMIT);
    set({ recentEpisodeIds: trimmed });
    saveRecentEpisodeIds(trimmed);
  },

  captureEpisodeIdFromUrl: () => {
    if (urlCaptured) return;
    urlCaptured = true;
    const fromUrl = normalizeEpisodeId(readHashId() || '');
    if (fromUrl) {
      set({ episodeSpecifyFromUrl: fromUrl, episodeSpecifyId: fromUrl });
    }
  },

  launchEpisodeSpecifyFromState: async () => {
    const target = get().episodeSpecifyFromUrl;
    if (!target) return false;
    set({ episodeSpecifyFromUrl: null });
    await get().showEpisodeSpecify(target, { autoSubmit: true, showViewerPlaceholder: false });
    return true;
  },

  lookupEpisode: async (rawId, options = {}) => {
    const episodeId = normalizeEpisodeId(rawId);
    nextEpisodeLookupId += 1;
    const lookupId = nextEpisodeLookupId;
    const isCurrentLookup = () => lookupId === nextEpisodeLookupId;

    set({ episodeSpecifyId: episodeId || null });
    setHashId(episodeId || null);

    const setStatus = (message: string, tone: EpisodeSpecifyStatus['tone']) => {
      set({
        episodeSpecifyStatus: { message, tone, visible: !!message },
      });
    };
    const setMeta = (queue: string, history: string, visible: boolean) => {
      set({
        episodeSpecifyMeta: {
          queue: visible ? queue : '—',
          history: visible ? history : '—',
          visible,
        },
      });
    };

    if (!episodeId) {
      set({ episodeSpecifyBusy: false });
      setStatus('Please enter an episode id.', 'warning');
      setMeta('—', '—', false);
      return;
    }
    if (options.showViewerPlaceholder) {
      get().showViewerLoadingShell(episodeId);
    }

    const { operator } = get();
    const intent = options.intent ?? get().episodeEntryIntent;

    if (intent !== 'admin-readonly' && !operator) {
      set({ episodeSpecifyBusy: false });
      set({ episodeSpecifyFromUrl: episodeId });
      showToast('Choose an operator first.');
      await get().showUserSelection();
      setMeta('—', '—', false);
      return;
    }

    set({ episodeSpecifyBusy: true });
    setStatus(
      intent === 'admin-readonly'
        ? 'Checking episode...'
        : intent === 'audit-claim'
          ? 'Checking and preparing audit...'
          : 'Checking and preparing episode...',
      'info'
    );
    setMeta('Looking up…', '—', true);

    try {
      let debug: EpisodeDebugResponse | null;
      let leaseId: string | null = null;
      let durationRepair: AnnotateNextResponse['duration_repair'];
      if (intent === 'admin-readonly') {
        debug = await fetchJSON<EpisodeDebugResponse>(
          `${apiPath('/admin/episodes')}/${encodeURIComponent(episodeId)}/view`
        );
      } else {
        if (intent === 'audit-claim' && !get().isAuditor && !get().isAdmin) {
          throw new Error('You are not an auditor.');
        }
        const claimPath =
          intent === 'audit-claim' ? '/audit/claim-specific' : '/annotate/claim-specific';
        const claim = await fetchJSON<AnnotateNextResponse>(apiPath(claimPath), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operator, episode_id: episodeId }),
        });
        if (!claim?.lease_id || !claim.episode || claim.episode.id !== episodeId) {
          throw new Error('Claim did not return the requested episode and lease');
        }
        leaseId = claim.lease_id;
        durationRepair = intent === 'operator-claim' ? claim.duration_repair : undefined;
        debug = {
          episode: claim.episode,
          in_pending: intent === 'operator-claim',
          in_leased: true,
        };
      }
      if (!isCurrentLookup()) return;
      if (!debug?.episode) throw new Error('Episode not found');
      get().addRecentEpisodeId(episodeId);
      const repaired = intent === 'operator-claim' ? durationRepair : undefined;
      setStatus(
        intent === 'admin-readonly'
          ? 'Episode opened in admin read-only mode.'
          : intent === 'audit-claim'
            ? 'Audit episode claimed.'
            : repaired?.applied
              ? `Duration repaired from ${repaired.old_duration}s to ${repaired.new_duration}s. Episode claimed.`
              : 'Episode claimed.',
        'success'
      );
      setMeta(
        debug.in_pending ? 'In pending queue' : 'Not in pending queue',
        intent === 'admin-readonly'
          ? 'Read only'
          : intent === 'audit-claim'
            ? 'Audit claim'
            : 'New production claim',
        true
      );
      if (options.autoOpen !== false) {
        get().showViewerLoadingShell(episodeId);
      }
      // The loading shell resets the previous annotation session, including its
      // lease. Apply the newly claimed lease only after that reset completes.
      set({
        leaseId,
        viewerReadOnly: intent === 'admin-readonly',
        leaseRecoveryRequired: false,
        ...(intent === 'audit-claim' ? { viewerMode: 'auditing' as const } : {}),
      });
      if (options.autoOpen !== false) {
        await get().openEpisode(episodeId, debug.episode.device_type_enum ?? null, debug, {
          readOnly: intent === 'admin-readonly',
        });
      }
    } catch (err) {
      if (!isCurrentLookup()) return;
      console.error('Episode lookup failed', err);
      const message = episodeClaimErrorMessage(err);
      if (
        intent === 'operator-claim' &&
        options.claimFailureReadOnly === true &&
        canOpenClaimFailureReadOnly(err)
      ) {
        get().showViewerLoadingShell(episodeId);
        const reason = apiErrorReason(err);
        set({
          leaseId: null,
          viewerReadOnly: true,
          leaseRecoveryRequired: ['leased_by_other', 'operator_has_active_lease'].includes(
            reason ?? ''
          ),
        });
        setStatus(message, 'warning');
        setMeta('Unavailable for editing', 'Read only', true);
        showToast(`${message} Opened read only.`);
        await get().openEpisode(episodeId, null, undefined, { readOnly: true });
        return;
      }
      set({ leaseId: null, viewerReadOnly: false, leaseRecoveryRequired: false });
      setStatus(message, 'error');
      setMeta('Unavailable', '—', true);
      get().showSection('episodeSpecify');
    } finally {
      if (isCurrentLookup()) set({ episodeSpecifyBusy: false });
    }
  },

  showEpisodeDebug: async (episodeId: string) => {
    set({ mode: 'episodeDebug' });
    get().showSection('episodeDebug');

    set({
      debugEpisodeId: episodeId,
      episodeDebugData: null,
      isLoading: true,
    });

    let data: EpisodeDebugResponse | null;
    try {
      data = await fetchJSON<EpisodeDebugResponse>(
        `${apiPath('/debug')}/${encodeURIComponent(episodeId)}`
      );
    } catch {
      showToast('Failed to load debug info');
      return;
    } finally {
      set({ isLoading: false });
    }
    if (!data) return;

    set({ episodeDebugData: data });
  },

  releaseEpisodeResources: () => {
    cancelActiveEpisodeLoad();
    set({ previewVideoUrl: null });
    resetPreviewVideoState();
    cleanupVideoPrefetcher();
    stopDraftAutosave();

    const { imageUrls } = get();
    if (imageUrls && imageUrls.length > 0) {
      imageUrls.forEach((url) => {
        if (typeof url === 'string' && url.startsWith('blob:')) {
          revokeBlobUrl(url);
        }
      });
    }
    set({
      imageUrls: [],
      images: [],
      labels: {},
      visited: new Set(),
      conflictFrames: new Set(),
      imagesLoading: false,
      loadingProgress: null,
      videoUnblockedSeconds: 0,
    });
    extractedSeconds.clear();
    useStore.setState({ previewVideoUrl: null, actionLabels: {}, actionLabelsLlmState: {} });
  },

  resetAnnotationSession: () => {
    get().releaseEpisodeResources();
    set({
      selectedEpisode: null,
      selectedDeviceTypeEnum: null,
      selectedCameraPosition: null,
      leaseId: null,
      sessionStartTime: null,
      currentEpisodeStatus: null,
      currentEpisodePriority: 9,
      currentEpisodeOperatorCount: 0,
      currentEpisodeVotes: null,
      currentOwnVoteAmendUntilMs: null,
      currentOwnVoteFinalizedAtMs: null,
      currentOwnAuditAmendUntilMs: null,
      currentEpisodeTaskQualityType: requiresExplicitTaskQuality ? null : TASK_QUALITY_NORMAL,
      currentEpisodeTaskQualityConflict: false,
      batchIndex: 0,
      batchOffset: 0,
      showVotingCompleteDialog: false,
      votingCompleteNoCommit: false,
      previewDurationMismatch: false,
      viewerMode: null,
      viewerReadOnly: false,
      leaseRecoveryRequired: false,
      imagesLoading: false,
      loadingProgress: null,
    });
  },

  dismissVotingCompleteDialog: () => {
    set({ showVotingCompleteDialog: false });
  },

  acknowledgePreviewDurationMismatch: async () => {
    // The preview can't cover the DDB duration, so this episode would never
    // commit. Drop the right lease (audit vs annotate) and go home;
    // backToEpisodeList() tears down the in-flight video load.
    const kind = get().viewerMode === 'auditing' ? 'audit' : 'annotate';
    set({ previewDurationMismatch: false });
    await get().releaseActiveLease(kind);
    await get().backToEpisodeList();
  },

  loadActionLabels: async (episodeId: string) => {
    try {
      const labels = await fetchActionLabels(episodeId);
      if (get().selectedEpisode !== episodeId) return;
      // tas_design prelabel: bot 离线给每段写一行 candidates_json + 空 final_text + null
      // selected_index 的"prefill"行；这种行进 actionLabelsLlmState 当 'ready' 状态，让
      // sidebar 显示 "Pick a candidate" + "Pick" 按钮，复用现场调 LLM 完成后的同一条 UX
      // 路径。人 pick 之后 save → 写回 action_labels 表（非空 final_text）→
      // 下次 loadActionLabels 走 savedMap 分支显示 'saved'，但仍把 candidates_json
      // 补进 actionLabelsLlmState，保证 Edit 能展开候选项。
      // 详见 docs/design-tas-prelabel/plan.md。
      const savedMap: Record<number, ActionLabel> = {};
      const llmStateMap: Record<number, LlmState> = {};
      for (const l of labels) {
        const isPrefill =
          l.final_text === '' && l.selected_index == null && l.candidates_json != null;
        if (l.candidates_json != null) {
          try {
            const candidates = JSON.parse(l.candidates_json) as LlmTask[];
            if (Array.isArray(candidates) && candidates.length > 0) {
              const llmState: LlmState = { status: 'ready', candidates };
              if (l.model != null) llmState.model = l.model;
              if (l.llm_duration_s != null) llmState.llmDurationS = l.llm_duration_s;
              llmStateMap[l.start_second] = llmState;
            }
          } catch {
            // malformed candidates_json — 跳过这行，sidebar 会显示 'unlabeled' 需要手动 Describe
          }
        }
        if (isPrefill) {
          continue;
        }
        savedMap[l.start_second] = l;
      }
      set({ actionLabels: savedMap, actionLabelsLlmState: llmStateMap });
    } catch (err) {
      console.warn('Failed to load action labels', err);
    }
  },

  triggerLlmForSegmentEnding: async (endSecond: number) => {
    if (get().viewerReadOnly) return;
    const {
      selectedEpisode,
      previewVideoUrl,
      statsCache,
      myStatsCache,
      currentEpisodeAnnotationFps,
    } = get();
    const features = myStatsCache?.features ?? statsCache?.features;
    if (!selectedEpisode || !features?.llm_action_labels) return;
    const loadId = activeEpisodeLoadId;
    let clipSourceUrl = previewVideoUrl;
    let transientClipUrl: string | null = null;
    if (!previewUrlCovers(clipSourceUrl, endSecond, loadId)) {
      transientClipUrl = createTransientVideoUrlFor(endSecond, loadId);
      clipSourceUrl = transientClipUrl;
    }
    if (!clipSourceUrl) return;

    const fps = Math.max(1, Math.floor(currentEpisodeAnnotationFps || 1));
    // endSecond is in float seconds; convert to bit index to search labels.
    const endBit = Math.round(endSecond * fps);

    // Find segment start: search backwards in bit-index space for the nearest
    // "new action" boundary (labels[bit] === false), then convert to seconds.
    let startSecond = 0;
    for (let bit = endBit - 1; bit > 0; bit--) {
      if (get().labels[String(bit)] === false) {
        startSecond = bit / fps;
        break;
      }
    }

    const existingStatus = get().actionLabelsLlmState[startSecond]?.status;
    if (existingStatus === 'clipping' || existingStatus === 'loading') return;

    const setLlm = (state: LlmState) =>
      set((s) => ({
        actionLabelsLlmState: { ...s.actionLabelsLlmState, [startSecond]: state },
      }));

    setLlm({ status: 'clipping', endSecond });
    try {
      const { extractActionClip } = await import('./clipVideo');
      const { analyzeAction } = await import('./llmClient');
      const clip = await extractActionClip(clipSourceUrl, startSecond, endSecond);
      if (get().selectedEpisode !== selectedEpisode || activeEpisodeLoadId !== loadId) {
        return;
      }
      setLlm({ status: 'loading', endSecond });
      const result = await analyzeAction(selectedEpisode, clip, startSecond, endSecond);
      if (get().selectedEpisode !== selectedEpisode || activeEpisodeLoadId !== loadId) {
        return;
      }
      setLlm({
        status: 'ready',
        endSecond,
        candidates: result.tasks,
        model: result.model,
        llmDurationS: result.llmDurationS,
      });
    } catch (err) {
      if (get().selectedEpisode !== selectedEpisode || activeEpisodeLoadId !== loadId) {
        return;
      }
      setLlm({ status: 'error', endSecond, error: String(err) });
    } finally {
      if (transientClipUrl) {
        revokePreviewBlobUrl(transientClipUrl);
      }
    }
  },

  saveActionLabelToStore: async (startSecond: number, payload: SaveActionLabelPayload) => {
    if (get().viewerReadOnly) return;
    const { selectedEpisode } = get();
    if (!selectedEpisode) return;
    const saved = await saveActionLabel(selectedEpisode, startSecond, payload);
    set((s) => {
      const actionLabelsLlmState = { ...s.actionLabelsLlmState };
      if (saved.candidates_json) {
        try {
          const candidates = JSON.parse(saved.candidates_json) as LlmTask[];
          if (Array.isArray(candidates) && candidates.length > 0) {
            actionLabelsLlmState[startSecond] = {
              status: 'ready',
              candidates,
              ...(saved.model != null ? { model: saved.model } : {}),
              ...(saved.llm_duration_s != null ? { llmDurationS: saved.llm_duration_s } : {}),
            };
          }
        } catch {
          delete actionLabelsLlmState[startSecond];
        }
      } else {
        delete actionLabelsLlmState[startSecond];
      }
      return { actionLabels: { ...s.actionLabels, [startSecond]: saved }, actionLabelsLlmState };
    });
  },

  deleteActionLabelFromStore: async (startSecond: number) => {
    if (get().viewerReadOnly) return;
    const { selectedEpisode } = get();
    if (!selectedEpisode) return;
    await deleteActionLabel(selectedEpisode, startSecond);
    set((s) => {
      const next = { ...s.actionLabels };
      delete next[startSecond];
      return { actionLabels: next };
    });
  },

  setTaskQualityType: (taskQualityType: TaskQualityType) => {
    if (get().viewerReadOnly) return;
    const { selectedEpisode, images } = get();
    if (!selectedEpisode) return;
    const nextType = normalizeTaskQualityType(taskQualityType);
    const patch: Partial<AppState> = {
      currentEpisodeTaskQualityType: nextType,
      currentEpisodeTaskQualityConflict: false,
    };
    if (nextType === TASK_QUALITY_STAGED_UNACCEPTABLE && !isResettingFlow) {
      // For secshot-style flows (true/false per-frame), mark all frames no-qualified.
      // For tas flows (boundary cuts), leave labels untouched — the frame values have
      // different semantics (cut positions), so blanket false would corrupt the payload.
      if (!isTasFlow) {
        const allFalseLabels: Record<string, boolean> = {};
        images.forEach((imgName) => {
          allFalseLabels[imgName] = false;
        });
        patch.labels = allFalseLabels;
      }
      patch.visited = new Set(images.map((_imgName, idx) => idx));
      patch.conflictFrames = new Set<string>();
    }
    set(patch);
    markDraftDirty();
    saveDraftSnapshot();
    if (isResettingFlow) {
      if (nextType === TASK_QUALITY_NORMAL) showToast('Task quality set to 2: skip');
      else if (nextType === TASK_QUALITY_STAGED_PASSIVE) showToast('Task quality set to 1-2: skip');
      else if (nextType === TASK_QUALITY_STAGED_RESET)
        showToast('Task quality set to 1-1: annotate');
      else if (nextType === TASK_QUALITY_STAGED_UNACCEPTABLE)
        showToast('Task quality set to 0: skip');
      else showToast('Task quality set to skip');
    } else if (nextType === TASK_QUALITY_NORMAL) showToast('Task quality set to Normal');
    else if (nextType === TASK_QUALITY_STAGED_PASSIVE) showToast('Task quality set to 1-2: staged');
    else if (nextType === TASK_QUALITY_STAGED_RESET) showToast('Task quality set to 1-1: reset');
    else if (nextType === TASK_QUALITY_STAGED_ACCEPTABLE)
      showToast('Task quality set to Staged Acceptable');
    else showToast('Task quality set to 0: unacceptable staged');
  },

  showViewerLoadingShell: (episodeId: string) => {
    get().resetAnnotationSession();
    set({ selectedEpisode: episodeId, imagesLoading: true });

    set({ loadingProgress: { text: 'Preparing preview frames...', percent: 0 } });

    get().showSection('viewer');
  },

  openEpisode: async (
    episodeId: string,
    deviceTypeEnum?: number | null,
    preloadedDebug?: EpisodeDebugResponse | null,
    options: { readOnly?: boolean } = {}
  ) => {
    const readOnly = options.readOnly === true;
    set({ viewerReadOnly: readOnly });
    // Tenant guard: secpose is not a bitmask/video flow. SecPoseViewer loads
    // /secpose-assets off `selectedEpisode`, so the shared video-preview loader
    // below must never run for it — otherwise it fetches a non-existent
    // <id>.preview.mp4 and 404s. The "next"/"audit" entry points already set
    // viewerMode themselves and skip openEpisode for secpose (the queue they
    // draw from guarantees the status matches); this branch covers every other
    // entry point (Episode-specify lookup, History review, admin episode list),
    // which call openEpisode directly without knowing annotate-vs-audit intent.
    // secpose never writes to the `votes` table (see FLOW !== 'secpose' branch
    // below), so — unlike the bitmask mode-determination further down — this
    // gates purely on mark_status instead of vote counts.
    if (FLOW === 'secpose') {
      const { isAdmin: isAdminNow, isAuditor: isAuditorNow } = get();
      let secposeDebug: EpisodeDebugResponse | null =
        preloadedDebug?.episode?.id === episodeId ? preloadedDebug : null;
      if (!secposeDebug) {
        try {
          secposeDebug = await fetchJSON<EpisodeDebugResponse>(
            `${apiPath('/debug')}/${encodeURIComponent(episodeId)}`
          );
        } catch (err) {
          console.error('Failed to load episode status', err);
          showToast('Failed to load episode. Try again later.');
          return;
        }
      }
      const markStatus = secposeDebug?.episode?.mark_status ?? 'pending';
      let nextMode: ViewerMode = 'annotating';
      let rejectionMsg: string | null = null;
      if (markStatus === 'completed') {
        if (isAdminNow || isAuditorNow) {
          nextMode = 'auditing';
        } else {
          nextMode = 'rejecting';
          rejectionMsg = 'Episode already completed by another annotator.';
        }
      } else if (markStatus === 'audited') {
        if (isAdminNow) {
          nextMode = 'auditing';
        } else {
          nextMode = 'rejecting';
          rejectionMsg = 'Episode has already been audited.';
        }
      } else if (markStatus !== 'pending') {
        // secpose has no 'voting' status; anything else is unexpected.
        nextMode = isAdminNow || isAuditorNow ? 'auditing' : 'rejecting';
        if (nextMode === 'rejecting') rejectionMsg = 'Episode is not available for annotation.';
      }
      set({
        selectedEpisode: episodeId,
        viewerMode: nextMode,
        rejectionMessage: rejectionMsg,
        imagesLoading: false,
      });
      get().showSection('viewer');
      return;
    }
    const episodeLoad = beginEpisodeLoad();
    const loadId = episodeLoad.id;
    const signal = episodeLoad.controller.signal;
    const isCurrentLoad = () => isEpisodeLoadCurrent(loadId, signal);

    cleanupVideoPrefetcher();
    stopDraftAutosave();
    const existingImageUrls = get().imageUrls;
    existingImageUrls.forEach((url) => {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        revokeBlobUrl(url);
      }
    });
    set({
      previewVideoUrl: null,
      imageUrls: [],
      images: [],
      labels: {},
      visited: new Set(),
      conflictFrames: new Set(),
      actionLabels: {},
      actionLabelsLlmState: {},
      videoUnblockedSeconds: 0,
    });
    resetPrefetchState();

    // Capture token AFTER cleanup so this call owns all subsequent viewer writes.
    const myToken = ++openEpisodeToken;
    const stillCurrent = () => isOpenEpisodeCurrent(myToken);

    const { operator, isAdmin, mode, episodes } = get();

    if (!operator && !readOnly) {
      cancelActiveEpisodeLoad();
      showToast('No operator selected');
      return;
    }
    if (mode === 'admin-episodes') {
      set({ previousSection: 'admin-episodes' });
    }

    let data: EpisodeDebugResponse | null =
      preloadedDebug?.episode?.id === episodeId ? preloadedDebug : null;
    if (!data) {
      try {
        const url = `${apiPath('/debug')}/${encodeURIComponent(episodeId)}`;
        data = await fetchJSON<EpisodeDebugResponse>(url, { signal });
      } catch (err) {
        if (!isCurrentLoad() || isAbortError(err)) return;
        console.error('Failed to load episode images', err);
        get().resetAnnotationSession();
        set({ continuous: false, leaseId: null });
        showToast('Failed to load episode. Try again later.');
        if (isAdmin) {
          get().showEpisodesManagement();
        } else {
          get().showComeBackLater();
        }
        return;
      }
    }

    if (!isCurrentLoad()) return;
    if (!data) {
      get().resetAnnotationSession();
      set({ continuous: false, leaseId: null });
      showToast('Failed to load episode. Try again later.');
      return;
    }
    get().addRecentEpisodeId(episodeId);

    const epi = episodes.find((e) => e.id === episodeId);
    const episode = data.episode ?? null;
    const markStatus = episode?.mark_status ?? epi?.mark_status ?? 'pending';
    const operatorCount = countUniqueVoteOperators(episode?.secshot_votes);
    if (!isAdmin && get().myStatsCache?.required_votes == null) {
      await get().fetchMyStatsData();
    }
    if (!stillCurrent()) return;
    const requiredVotes =
      get().myStatsCache?.required_votes ?? get().statsCache?.required_votes ?? 2;

    // Mode Determination Logic
    let nextMode: 'annotating' | 'reviewing' | 'auditing' | 'rejecting' = 'annotating';
    let rejectionMsg: string | null = null;
    const ownVote = findVoteForOperator(episode?.secshot_votes ?? undefined, operator);
    const hasAnnotated = !!ownVote;
    const features = get().myStatsCache?.features ?? get().statsCache?.features;
    const ownVoteAmendUntilMs = ownVote?.amend_until_ms ?? null;
    const ownVoteFinalizedAtMs = ownVote?.finalized_at_ms ?? null;
    const canAmendOwnVote =
      !!features?.annotator_amend_confirm &&
      !!ownVote &&
      ownVoteFinalizedAtMs == null &&
      ownVoteAmendUntilMs != null &&
      ownVoteAmendUntilMs > Date.now();
    const ownAuditDraft =
      episode?.secshot_audit?.is_draft === true && episode.secshot_audit.operator === operator
        ? episode.secshot_audit
        : null;
    const ownAuditAmendUntilMs = ownAuditDraft?.amend_until_ms ?? null;
    const isAuditor = get().isAuditor;
    const votingCompleteNoCommit =
      operatorCount >= requiredVotes && !isAuditor && !isAdmin && !canAmendOwnVote;
    const showVotingCompleteDialog =
      operatorCount >= requiredVotes && hasAnnotated && !isAuditor && !isAdmin;

    if (operatorCount >= requiredVotes && !hasAnnotated && !isAuditor && !isAdmin) {
      nextMode = 'rejecting';
      rejectionMsg =
        markStatus === 'completed'
          ? 'The episode has been completed, but you are not its annotator or auditor.'
          : 'This episode already has 2 annotators. No further annotation allowed.';
    } else if (markStatus === 'pending') {
      nextMode = 'annotating';
    } else if (markStatus === 'voting') {
      if (hasAnnotated) nextMode = 'reviewing';
      else nextMode = 'annotating';
    } else if (markStatus === 'completed') {
      if (ownAuditDraft) nextMode = 'auditing';
      else if (hasAnnotated) nextMode = 'reviewing';
      else if (isAuditor || isAdmin) nextMode = 'auditing';
      else {
        nextMode = 'rejecting';
        rejectionMsg = 'The episode has been completed, but you are not its annotator or auditor.';
      }
    } else if (markStatus === 'audited') {
      const auditOperator = episode?.secshot_audit?.operator ?? null;
      if (auditOperator === operator || isAdmin) nextMode = 'auditing';
      else if (hasAnnotated) nextMode = 'reviewing';
      else {
        nextMode = 'rejecting';
        rejectionMsg = 'The episode has been audited, but you are not its auditor.';
      }
    }

    set({
      viewerMode: nextMode,
      rejectionMessage: rejectionMsg,
      showVotingCompleteDialog,
      votingCompleteNoCommit,
      previewDurationMismatch: false,
    });

    if (nextMode === 'rejecting') {
      get().showSection('viewer');
      set({ imagesLoading: false });
      finishEpisodeLoad(loadId);
      return;
    }

    const resolvedDeviceTypeEnum =
      coerceDeviceTypeEnum(deviceTypeEnum) ??
      resolveDeviceTypeEnum(episode ?? undefined) ??
      resolveDeviceTypeEnum(epi);
    const resolvedCameraPosition =
      resolveCameraPosition(episode ?? undefined) ?? resolveCameraPosition(epi);

    // Hidden golden assignments must not put the episode_id in the URL hash:
    // a refresh would route through episodeSpecifyFromUrl and load the real
    // production episode (which has Mia's audit/vote already attached),
    // leaking the ground truth and breaking the calibration. Operator can
    // still resume by clicking Start to Annotate — the scheduler picks up
    // the in-progress attempt server-side. Production tasks keep the hash.
    const isHiddenGolden = (get().leaseId ?? '').startsWith('g:');
    if (typeof window !== 'undefined' && !isHiddenGolden) {
      window.location.hash = `#id/${episodeId}`;
    }

    const taskQualityState = resolveInitialTaskQuality(episode, nextMode, operator);

    const episodeFps = Math.max(1, Math.floor(episode?.annotation_fps ?? 1));
    const episodePriority =
      typeof episode?.priority === 'number'
        ? Math.trunc(episode.priority)
        : typeof epi?.priority === 'number'
          ? Math.trunc(epi.priority)
          : 9;

    set({
      currentEpisodeStatus: markStatus,
      currentEpisodePriority: episodePriority,
      currentEpisodeOperatorCount: operatorCount,
      currentEpisodeVotes: episode?.secshot_votes ?? null,
      currentOwnVoteAmendUntilMs: ownVoteAmendUntilMs,
      currentOwnVoteFinalizedAtMs: ownVoteFinalizedAtMs,
      currentOwnAuditAmendUntilMs: ownAuditAmendUntilMs,
      currentEpisodeTaskQualityType: taskQualityState.type,
      currentEpisodeTaskQualityConflict: taskQualityState.conflict,
      currentEpisodeAnnotationFps: episodeFps,
      // Hidden golden never reads/writes production action labels — clear
      // any leftover state from a prior production task on this episode_id.
      ...(isHiddenGolden ? { actionLabels: {}, actionLabelsLlmState: {} } : {}),
      selectedDeviceTypeEnum: resolvedDeviceTypeEnum,
      selectedCameraPosition: resolvedCameraPosition,
      selectedEpisode: episodeId,
      visited: new Set(),
      batchIndex: 0,
      batchOffset: 0,
      imagesLoading: true,
    });

    set({ loadingProgress: { text: 'Preparing preview frames...', percent: 0 } });
    get().showSection('viewer');
    stopDraftAutosave();

    const updateLoaderProgress = (msg: string, percent: number) => {
      if (!isCurrentLoad()) return;
      if (msg.startsWith('Downloading video')) {
        activeDownloadProgress = { text: msg, percent };
      }
      set({ loadingProgress: { text: msg, percent } });
    };

    const handleFrameExtracted = (second: number, url: string) => {
      if (!isCurrentLoad()) {
        revokeBlobUrl(url);
        return;
      }
      const { imageUrls } = get();
      if (!imageUrls || second < 0 || second >= imageUrls.length) {
        revokeBlobUrl(url);
        return;
      }
      const newUrls = [...imageUrls];
      newUrls[second] = url;
      set({ imageUrls: newUrls });

      if (!extractedSeconds.has(second)) {
        extractedSeconds.add(second);
        const targetSeconds = Math.max(prefetchTargetSeconds, 1);
        const readySeconds = Math.min(extractedSeconds.size, targetSeconds);
        const percent = Math.min(100, Math.round((readySeconds / targetSeconds) * 100));
        updateLoaderProgress('Preparing preview frames...', percent);
      }
    };

    let extractor: VideoFrameExtractor | null = null;
    let cleanupPreviewUrl: (() => void) | null = null;
    let keepLoadActiveAfterOpen = false;
    let initializedFromVideo = false;
    let restoredStart: number | null = null;
    let firstReadySettled = false;
    let activatedLoadedBytes = 0;
    try {
      const videoUrl = resolveVideoUrl(episodeId);
      const extractorBlobUrls = new Set<string>();
      cleanupPreviewUrl = () => {
        const previewUrl = get().previewVideoUrl;
        const shouldClearPreview = previewUrl !== null && extractorBlobUrls.has(previewUrl);
        extractorBlobUrls.forEach((url) => {
          revokePreviewBlobUrl(url);
        });
        extractorBlobUrls.clear();
        if (shouldClearPreview) {
          set({ previewVideoUrl: null });
        }
      };

      const makeCleanup = (url: string) => () => {
        extractorBlobUrls.delete(url);
        revokePreviewBlobUrl(url);
        if (get().previewVideoUrl === url) {
          set({ previewVideoUrl: null });
        }
      };

      // Block episodes whose preview is too short to ever pass the commit guard
      // ("Encoded payload too short"), sending the operator back to release it
      // up front instead of after labeling. `totalSeconds` is the exact cell
      // count the commit encodes; the guard lives in previewDurationCheck.ts.
      // Hidden golden has no comparable DDB row of its own, so it's exempt.
      const flagPreviewDurationMismatchIfTooShort = (totalSeconds: number): void => {
        if (isHiddenGolden) return;
        if (isPreviewDurationMismatch(totalSeconds, episode?.duration, episodeFps)) {
          set({ previewDurationMismatch: true });
        }
      };

      const setupEpisodeState = (totalSeconds: number): void => {
        const imageIds = Array.from({ length: totalSeconds }, (_, idx) => String(idx));
        const imageUrls = new Array(totalSeconds).fill('');
        totalPrefetchSeconds = totalSeconds;

        if (nextMode === 'auditing') {
          const uniqueVotes = new Map<string, SecshotVote>();
          (episode?.secshot_votes || []).forEach((v) => {
            if (v.operator && !uniqueVotes.has(v.operator)) uniqueVotes.set(v.operator, v);
          });
          const sortedVotes = Array.from(uniqueVotes.values()).sort(
            (a, b) => (a.completed_at_ms || 0) - (b.completed_at_ms || 0)
          );

          const newLabels: Record<string, boolean | null> = {};
          const newVisited = new Set<number>();

          if (sortedVotes.length >= 2) {
            const v1 = sortedVotes[0]!;
            const v2 = sortedVotes[1]!;
            const set1 = decodeVoteToSet(v1, totalSeconds);
            const set2 = decodeVoteToSet(v2, totalSeconds);

            if (!set1 || !set2) {
              for (let i = 0; i < totalSeconds; i++) {
                newLabels[String(i)] = null;
              }
            } else if (isTasFlow) {
              const result = buildTasToleranceAuditLabels(set1, set2, totalSeconds, episodeFps);
              Object.assign(newLabels, result.labels);
              for (const idx of result.visited) newVisited.add(idx);
            } else {
              for (let i = 0; i < totalSeconds; i++) {
                const bit1 = set1.has(i);
                const bit2 = set2.has(i);

                if (bit1 === bit2) {
                  newLabels[String(i)] = bit1;
                  newVisited.add(i);
                } else {
                  newLabels[String(i)] = null;
                }
              }
            }
          } else if (sortedVotes.length === 1) {
            const set1 = decodeVoteToSet(sortedVotes[0]!, totalSeconds);
            for (let i = 0; i < totalSeconds; i++) {
              newLabels[String(i)] = set1 ? set1.has(i) : null;
            }
          } else {
            for (let i = 0; i < totalSeconds; i++) newLabels[String(i)] = null;
          }

          const conflictFrames = new Set<string>(
            Object.entries(newLabels)
              .filter(([, v]) => v === null)
              .map(([k]) => k)
          );
          const savedAudit = episode?.secshot_audit;
          if (savedAudit?.encoded) {
            const auditSet = decodeVoteToSet(savedAudit, totalSeconds);
            if (auditSet) {
              for (let i = 0; i < totalSeconds; i++) {
                newLabels[String(i)] = auditSet.has(i);
                newVisited.add(i);
              }
            }
          }
          set({
            images: imageIds,
            labels: newLabels,
            visited: newVisited,
            imageUrls,
            conflictFrames,
          });
        } else if (nextMode === 'reviewing') {
          const decodedLabels = decodeVoteToLabels(
            totalSeconds,
            episode?.secshot_votes ?? null,
            operator
          );
          const allVisited = new Set<number>();
          for (let i = 0; i < totalSeconds; i++) allVisited.add(i);
          set({
            images: imageIds,
            labels: decodedLabels,
            visited: allVisited,
            imageUrls,
            conflictFrames: new Set<string>(),
          });
        } else {
          // Prelabel: for tas flows, if the model bot has already voted on this episode,
          // decode its vote as the initial draft and mark all frames visited. This only
          // applies to tas flows — for secshot the bot votes as an independent first voter
          // and annotators must label from scratch without seeing the bot result.
          // applyDraftIfAvailable (called later) will override per-frame for any
          // locally-saved draft. See docs/design-tas-prelabel/plan.md.
          const modelOperator = isTasFlow
            ? (get().statsCache?.model_operator ?? get().myStatsCache?.model_operator ?? null)
            : null;
          const botVote =
            modelOperator != null
              ? ((episode?.secshot_votes || []).find(
                  (v) => v?.operator === modelOperator && v.encoded
                ) ?? null)
              : null;
          if (botVote) {
            const botSet = decodeVoteToSet(botVote, totalSeconds);
            const labels: Record<string, boolean> = {};
            const allVisited = new Set<number>();
            for (let i = 0; i < totalSeconds; i += 1) {
              labels[String(i)] = botSet ? botSet.has(i) : true;
              allVisited.add(i);
            }
            set({
              images: imageIds,
              labels,
              visited: allVisited,
              imageUrls,
              conflictFrames: new Set<string>(),
            });
          } else {
            const decodedLabels = decodeVoteToLabels(totalSeconds, null, operator);
            set({
              images: imageIds,
              labels: decodedLabels,
              visited: new Set(),
              imageUrls,
              conflictFrames: new Set<string>(),
            });
          }
        }

        restoredStart = consumeRestoredStartSecond();
        if (
          restoredStart === null &&
          !isHiddenGolden &&
          markStatus !== 'completed' &&
          markStatus !== 'audited'
        ) {
          const draft = loadDraftForOperator(operator, episodeId);
          if (draft && draft.episodeId === episodeId) {
            restoredStart = draft.batchIndex * BATCH_SIZE + draft.batchOffset;
          }
        }

        const initialBatchIndex =
          restoredStart != null ? Math.floor(restoredStart / BATCH_SIZE) : 0;
        const initialBatchOffset = restoredStart != null ? restoredStart % BATCH_SIZE : 0;

        set({
          batchIndex: initialBatchIndex,
          batchOffset: initialBatchOffset,
        });
      };

      const ensureCurrentWindowAvailable = async (): Promise<boolean> => {
        if (!extractor || totalPrefetchSeconds <= 0) return false;
        const { batchIndex, batchOffset, imageUrls } = get();
        const start = batchIndex * BATCH_SIZE + batchOffset;
        const requiredPreviewEnd = Math.min(totalPrefetchSeconds, start + BATCH_SIZE);
        const prefetch = scheduleBalancedFramePrefetch(start, imageUrls);
        if (prefetch.blocked) {
          set({ imagesLoading: true, loadingProgress: activeDownloadProgress });
          return false;
        }
        const currentPreviewUrl = get().previewVideoUrl;
        if (
          requiredPreviewEnd > start &&
          !previewUrlCovers(currentPreviewUrl, requiredPreviewEnd, loadId)
        ) {
          const nextPreviewUrl = createPreviewUrlFor(requiredPreviewEnd, loadId);
          if (nextPreviewUrl) {
            set({ previewVideoUrl: nextPreviewUrl });
            releasePreviewVideoUrl(currentPreviewUrl);
          }
        }
        if (!prefetch.visibleSeconds.length) {
          set({ imagesLoading: false });
          return true;
        }

        set({
          imagesLoading: true,
          loadingProgress: { text: 'Preparing preview frames...', percent: 0 },
        });
        await extractor.waitForSeconds(prefetch.visibleSeconds);
        if (!isCurrentLoad()) return false;
        set({ imagesLoading: false });
        return true;
      };

      const activateVideoBlob = async (snapshot: VideoChunkSnapshot): Promise<boolean> => {
        if (!isCurrentLoad()) return false;
        if (!snapshot.complete && snapshot.loadedBytes <= activatedLoadedBytes) return false;
        const url = URL.createObjectURL(snapshot.blob);
        extractorBlobUrls.add(url);
        let created: VideoFrameExtractor;
        try {
          created = await VideoFrameExtractor.create(
            url,
            handleFrameExtracted,
            makeCleanup(url),
            signal,
            { fps: episodeFps }
          );
        } catch (err) {
          extractorBlobUrls.delete(url);
          revokePreviewBlobUrl(url);
          if (snapshot.complete) throw err;
          return false;
        }

        if (!isCurrentLoad()) {
          created.dispose();
          return false;
        }
        if (snapshot.loadedBytes < activatedLoadedBytes) {
          created.dispose();
          return false;
        }
        activatedLoadedBytes = Math.max(activatedLoadedBytes, snapshot.loadedBytes);

        const oldExtractor = extractor;
        extractor = created;
        frameExtractor = created;
        registerFrameExtractor(created);
        oldExtractor?.dispose();

        const totalSeconds = created.totalSeconds;
        if (!initializedFromVideo) {
          initializedFromVideo = true;
          flagPreviewDurationMismatchIfTooShort(totalSeconds);
          setupEpisodeState(totalSeconds);
          updateLoaderProgress('Preparing preview frames...', 0);
        }

        const estimatedAvailableSeconds =
          snapshot.totalBytes && snapshot.totalBytes > 0
            ? Math.max(
                1,
                Math.min(
                  totalSeconds,
                  Math.floor((snapshot.loadedBytes / snapshot.totalBytes) * totalSeconds)
                )
              )
            : 0;
        const nextAvailableSeconds = snapshot.complete ? totalSeconds : estimatedAvailableSeconds;
        availablePrefetchSeconds = Math.max(availablePrefetchSeconds, nextAvailableSeconds);
        rememberLatestPreviewBlob(snapshot.blob, availablePrefetchSeconds, loadId);
        set({ videoUnblockedSeconds: availablePrefetchSeconds });

        return ensureCurrentWindowAvailable();
      };

      const firstReadyPromise = new Promise<void>((resolve, reject) => {
        const resolveFirstReady = (): void => {
          if (firstReadySettled) return;
          firstReadySettled = true;
          resolve();
        };
        const rejectFirstReady = (err: unknown): void => {
          if (firstReadySettled) return;
          firstReadySettled = true;
          reject(err instanceof Error ? err : new Error(String(err)));
        };

        void (async () => {
          let blob: Blob | null = null;
          try {
            blob = await loadCachedVideo(episodeId);
          } catch (err) {
            console.warn('Failed to read cached video', err);
          }
          if (!isCurrentLoad()) throw createAbortError();
          if (blob) {
            console.debug('Using cached video for', episodeId);
            const available = await activateVideoBlob({
              blob,
              loadedBytes: blob.size,
              totalBytes: blob.size,
              complete: true,
            });
            if (available) resolveFirstReady();
            else rejectFirstReady(new Error('Cached video did not become available'));
            return;
          }

          updateLoaderProgress('Downloading video...', 0);
          keepLoadActiveAfterOpen = true;
          const downloadPromise = downloadVideoWithResume(videoUrl, {
            signal,
            onProgress: ({ text, percent }) => updateLoaderProgress(text, percent),
            onChunkComplete: (snapshot) => {
              void activateVideoBlob(snapshot)
                .then((available) => {
                  if (available) resolveFirstReady();
                })
                .catch((err) => {
                  if (snapshot.complete) rejectFirstReady(err);
                });
            },
          });

          void downloadPromise
            .then((fullBlob) => {
              if (!isCurrentLoad()) return;
              console.debug('Cached downloaded video for', episodeId);
              void storeCachedVideo(episodeId, fullBlob);
            })
            .catch((err) => {
              if (!isCurrentLoad() || isAbortError(err)) return;
              if (!initializedFromVideo) {
                rejectFirstReady(err);
                return;
              }
              showToast('Video download interrupted. Loaded frames remain available.');
            })
            .finally(() => {
              if (isCurrentLoad()) {
                keepLoadActiveAfterOpen = false;
                finishEpisodeLoad(loadId);
              }
            });
        })().catch(rejectFirstReady);
      });

      await firstReadyPromise;
    } catch (err) {
      if (!isCurrentLoad() || isAbortError(err)) {
        const currentExtractor = extractor as VideoFrameExtractor | null;
        if (currentExtractor) {
          currentExtractor.dispose();
        } else {
          cleanupPreviewUrl?.();
        }
        return;
      }
      console.error('Failed to load preview video frames:', err);
      showToast('Failed to load preview video');
      const currentExtractor = extractor as VideoFrameExtractor | null;
      if (currentExtractor) {
        currentExtractor.dispose();
      } else {
        cleanupPreviewUrl?.();
      }
      set({ previewVideoUrl: null });
      resetPrefetchState();

      const decodedLabels = decodeVoteToLabels(0, episode?.secshot_votes ?? null, operator);
      set({ images: [], labels: decodedLabels, imageUrls: [], videoUnblockedSeconds: 0 });
    } finally {
      if (isCurrentLoad()) {
        set({ imagesLoading: false });
        if (!keepLoadActiveAfterOpen) {
          finishEpisodeLoad(loadId);
        }
      }
    }

    if (!isCurrentLoad()) return;
    if (readOnly) {
      stopDraftAutosave();
      set({ sessionStartTime: null });
    } else if (markStatus === 'completed') {
      clearDraftForOperator(operator, episodeId);
      clearAccumulatedMs(operator, episodeId);
    }
    if (!isHiddenGolden && !readOnly) {
      applyDraftIfAvailable();
      if (markStatus !== 'completed') {
        startDraftAutosave();
      }
    }
    // Push sessionStartTime back by whatever this operator already accumulated
    // on this episode in a prior session (survives reloads/resumes, cleared on
    // successful commit) — Date.now() - sessionStartTime then reads as
    // cumulative time with no other code needing to change.
    if (!readOnly) {
      const now = Date.now();
      const priorAccumulatedMs = isHiddenGolden ? 0 : loadAccumulatedMs(operator, episodeId);
      set({ sessionStartTime: now - priorAccumulatedMs });
    }

    const llmFeatures = get().myStatsCache?.features ?? get().statsCache?.features;
    if (!isHiddenGolden && llmFeatures?.llm_action_labels) {
      void get().loadActionLabels(episodeId);
    }
  },

  commit: async (sessionDurationMs?: number) => {
    if (get().viewerReadOnly) return;
    const {
      imagesLoading,
      images,
      visited,
      operator,
      selectedEpisode,
      labels,
      leaseId,
      episodes,
      currentEpisodeOperatorCount,
      continuous,
      isAdmin,
      viewerMode,
      isCommitting,
      votingCompleteNoCommit,
      currentEpisodeTaskQualityType,
      currentEpisodeTaskQualityConflict,
      actionLabels,
      statsCache,
      myStatsCache,
    } = get();
    const commitFeatures = myStatsCache?.features ?? statsCache?.features;

    if (isCommitting) {
      return;
    }
    if (votingCompleteNoCommit) {
      showToast('This episode already has 2 annotators. Only auditor or admin can commit.');
      return;
    }
    if (imagesLoading) {
      showToast('Please wait for frames to finish loading.');
      return;
    }
    const isHiddenGoldenCommit = (leaseId ?? '').startsWith('g:');
    if (
      !isHiddenGoldenCommit &&
      requiresExplicitTaskQuality &&
      currentEpisodeTaskQualityType == null
    ) {
      showToast('Please select task quality before committing.');
      return;
    }
    const selectedTaskQualityType = currentEpisodeTaskQualityType ?? TASK_QUALITY_NORMAL;
    // In the resetting flow only the "1-1: annotate" option (STAGED_RESET)
    // requires reviewing every second; the three "skip" options (2/1-2/0)
    // can be committed without a full pass.
    const canCommitWithoutFullReview =
      isResettingFlow && currentEpisodeTaskQualityType !== TASK_QUALITY_STAGED_RESET;
    if (images.length === 0 || (!canCommitWithoutFullReview && visited.size < images.length)) {
      showToast('Please review all seconds before committing.');
      return;
    }
    if (viewerMode === 'auditing') {
      const hasUnresolvedConflict = images.some((img) => labels[img] === null);
      if (hasUnresolvedConflict && !canCommitWithoutFullReview) {
        showToast('Please make a decision on all conflict frames before committing.');
        return;
      }
      if (currentEpisodeTaskQualityConflict) {
        showToast('Please resolve task quality conflict before committing.');
        return;
      }
    }
    if (
      !isHiddenGoldenCommit &&
      isTasFlow &&
      commitFeatures?.llm_action_labels &&
      selectedTaskQualityType !== TASK_QUALITY_STAGED_UNACCEPTABLE
    ) {
      const totalBits = images.length;
      const fps = Math.max(1, Math.floor(get().currentEpisodeAnnotationFps || 1));
      // segmentStarts: bit indices of each segment's first cell.
      const segmentStarts: number[] = [0];
      for (let i = 1; i < totalBits; i++) {
        if (labels[String(i)] === false) segmentStarts.push(i);
      }
      // actionLabels is keyed by float seconds; convert bit index before lookup.
      const unsaved = segmentStarts.filter((bitIdx) => !actionLabels[bitIdx / fps]);
      if (unsaved.length > 0) {
        showToast('Please save action labels for all segments before committing.');
        return;
      }
    }

    if (!operator) {
      showToast('No operator selected');
      return;
    }

    if (!selectedEpisode) {
      showToast('No episode selected');
      return;
    }

    const encodedPayload = encodeLabelsToBase64Url(images, labels);

    const payload = {
      encoded: encodedPayload.encoded,
      operator: operator,
      lease_id: leaseId || void 0,
      sessionDurationMs,
      task_quality_type: selectedTaskQualityType,
    };

    set({ isCommitting: true });

    const endpoint =
      viewerMode === 'auditing'
        ? `${apiPath('/audit')}/${encodeURIComponent(selectedEpisode)}`
        : `${apiPath('/commit')}/${encodeURIComponent(selectedEpisode)}`;

    let commitResult: CommitResponse | null = null;
    try {
      const result = await fetchJSON<CommitResponse>(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (result?.status !== 'ok') {
        throw new Error('Non-ok status');
      }
      commitResult = result;
      const completed = result?.completed === true;
      const newStatus =
        viewerMode === 'auditing'
          ? completed
            ? 'audited'
            : 'completed'
          : completed
            ? 'completed'
            : 'voting';
      const alreadyVoted = (get().currentEpisodeVotes ?? []).some((v) => v.operator === operator);
      const newCount =
        viewerMode === 'auditing' || alreadyVoted
          ? currentEpisodeOperatorCount
          : currentEpisodeOperatorCount > 0
            ? currentEpisodeOperatorCount + 1
            : 1;

      set({ currentEpisodeStatus: newStatus, currentEpisodeOperatorCount: newCount });

      const idx = episodes.findIndex((e) => e.id === selectedEpisode);
      if (idx >= 0) {
        const newEpisodes = [...episodes];
        const existing = newEpisodes[idx] as EpisodeSummary;
        const ep: EpisodeSummary = { ...existing };
        ep.mark_status = newStatus;
        if (viewerMode === 'auditing') {
          ep.task_quality_type = selectedTaskQualityType;
          ep.task_quality_conflict = false;
          ep.audit_draft_operator = completed ? null : operator;
          ep.audit_amend_until_ms = completed ? null : (result.amend_until_ms ?? null);
          ep.audit_submitted_at_ms = completed ? null : Date.now();
        }
        if (viewerMode !== 'auditing') {
          const ops = ep.operators ? [...ep.operators] : [];
          if (!ops.includes(operator)) ops.push(operator);
          ep.operators = ops;
        }
        newEpisodes[idx] = ep;
        set({ episodes: newEpisodes });

        get().invalidateEpisodeSortCache();
        get().updateCachedSummaryPage();
      }
    } catch (err) {
      console.error('Commit failed', err);
      let message = 'Commit failed';
      let leaseRecoveryRequired = false;
      if (err instanceof HttpError) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed && typeof parsed.detail === 'string') {
            message = parsed.detail;
          }
          leaseRecoveryRequired = [
            'lease_id_required',
            'lease_expired',
            'lease_mismatch',
            'leased_by_other',
          ].includes(typeof parsed?.reason === 'string' ? parsed.reason : '');
        } catch {
          // ignore parsing error
        }
      }
      if (leaseRecoveryRequired) {
        set({ leaseId: null, viewerReadOnly: true, leaseRecoveryRequired: true });
        showToast(`${message}. Reclaim editing access to save your changes.`);
      } else {
        showToast(message);
      }
      return;
    } finally {
      set({ isCommitting: false });
    }

    clearDraftForOperator(operator, selectedEpisode);
    clearAccumulatedMs(operator, selectedEpisode);
    const shouldStayForAnnotatorConfirm =
      viewerMode !== 'auditing' &&
      !isHiddenGoldenCommit &&
      commitFeatures?.annotator_amend_confirm === true &&
      commitResult?.completed !== true;
    const shouldStayForAuditConfirm =
      viewerMode === 'auditing' &&
      commitFeatures?.auditor_amend_confirm === true &&
      commitResult?.completed !== true;
    const shouldStayForConfirm = shouldStayForAnnotatorConfirm || shouldStayForAuditConfirm;
    showToast(
      shouldStayForAuditConfirm
        ? 'Audit saved. Confirm when ready.'
        : shouldStayForAnnotatorConfirm
          ? 'Saved. Confirm when ready.'
          : 'Commit successful'
    );
    void (async () => {
      await get().fetchMyStatsData(true);
      if (get().isAdmin) await get().fetchStatsData(true);
      await get().updateDashboardMetrics();
    })();

    if (shouldStayForConfirm) {
      set({
        leaseId: null,
        viewerReadOnly: true,
        leaseRecoveryRequired: true,
        viewerMode: shouldStayForAuditConfirm ? 'auditing' : 'reviewing',
        ...(shouldStayForAuditConfirm
          ? {
              currentOwnAuditAmendUntilMs:
                commitResult?.amend_until_ms ?? get().currentOwnAuditAmendUntilMs,
            }
          : {
              currentOwnVoteAmendUntilMs:
                commitResult?.amend_until_ms ?? get().currentOwnVoteAmendUntilMs,
              currentOwnVoteFinalizedAtMs: commitResult?.finalized_at_ms ?? null,
            }),
      });
      return;
    }

    // Handle post commit
    if (continuous && operator && !isAdmin) {
      await get().startOperatorRandom(operator);
    } else {
      await get().backToEpisodeList(true);
    }
  },

  reclaimCurrentEpisodeLease: async () => {
    const { operator, selectedEpisode, isCommitting, currentOwnAuditAmendUntilMs } = get();
    if (isCommitting) return;
    if (!operator || !selectedEpisode) {
      showToast('No episode selected');
      return;
    }

    set({ isCommitting: true });
    try {
      const claimPath =
        currentOwnAuditAmendUntilMs != null ? '/audit/claim-specific' : '/annotate/claim-specific';
      const claim = await fetchJSON<AnnotateNextResponse>(apiPath(claimPath), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operator, episode_id: selectedEpisode }),
      });
      if (!claim?.lease_id || !claim.episode || claim.episode.id !== selectedEpisode) {
        throw new Error('Claim did not return the requested episode and lease');
      }
      if (get().selectedEpisode !== selectedEpisode || get().operator !== operator) return;
      set({
        leaseId: claim.lease_id,
        viewerReadOnly: false,
        leaseRecoveryRequired: false,
      });
      showToast('Editing access restored. Your unsaved changes are still here.');
    } catch (err) {
      console.error('Lease reclaim failed', err);
      showToast(episodeClaimErrorMessage(err));
    } finally {
      set({ isCommitting: false });
    }
  },

  confirmOwnVote: async () => {
    const { operator, selectedEpisode, isCommitting } = get();
    if (isCommitting) return;
    if (!operator || !selectedEpisode) {
      showToast('No episode selected');
      return;
    }

    set({ isCommitting: true });
    try {
      const result = await fetchJSON<CommitResponse>(
        `${apiPath('/me/votes')}/${encodeURIComponent(selectedEpisode)}/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operator }),
        }
      );
      if (result?.status !== 'ok') throw new Error('Non-ok status');
      showToast('Confirmed');
      await get().fetchMyStatsData(true);
      if (get().isAdmin) await get().fetchStatsData(true);
      await get().updateDashboardMetrics();
      await get().backToEpisodeList(true);
    } catch (err) {
      console.error('Confirm failed', err);
      let message = 'Confirm failed';
      if (err instanceof HttpError) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed && typeof parsed.detail === 'string') {
            message = parsed.detail;
          }
        } catch {
          // ignore parsing error
        }
      }
      showToast(message);
    } finally {
      set({ isCommitting: false });
    }
  },

  confirmOwnAudit: async () => {
    const { operator, selectedEpisode, isCommitting } = get();
    if (isCommitting) return;
    if (!operator || !selectedEpisode) {
      showToast('No episode selected');
      return;
    }

    set({ isCommitting: true });
    try {
      const result = await fetchJSON<CommitResponse>(
        `${apiPath('/me/audits')}/${encodeURIComponent(selectedEpisode)}/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operator }),
        }
      );
      if (result?.status !== 'ok') throw new Error('Non-ok status');
      showToast('Audit confirmed');
      await get().fetchMyStatsData(true);
      if (get().isAdmin) await get().fetchStatsData(true);
      await get().updateDashboardMetrics();
      await get().backToEpisodeList(true);
    } catch (err) {
      console.error('Audit confirm failed', err);
      let message = 'Audit confirm failed';
      if (err instanceof HttpError) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed && typeof parsed.detail === 'string') message = parsed.detail;
        } catch {
          // Keep the generic message when the response is not JSON.
        }
      }
      showToast(message);
    } finally {
      set({ isCommitting: false });
    }
  },

  toggleRange: (startIdx: number, count: number) => {
    if (get().viewerReadOnly) return;
    const { images, visited, labels, viewerMode } = get();
    if (count <= 1) {
      // Single-cell case: defer to toggle() which runs full LLM bookkeeping.
      get().toggle(startIdx);
      return;
    }
    const leadName = images[startIdx];
    if (!leadName) return;

    // Compute the lead bit's new value using the same state machine as toggle().
    const current = labels[leadName];
    let nextValue: boolean | null;
    if (isTasFlow && viewerMode === 'auditing') {
      if (current === null) nextValue = true;
      else if (current === true) nextValue = false;
      else nextValue = true;
    } else {
      nextValue = !current;
    }

    const newVisited = new Set(visited);
    const newLabels = { ...labels };
    for (let k = 0; k < count; k += 1) {
      const idx = startIdx + k;
      const name = images[idx];
      if (!name) continue;
      newVisited.add(idx);

      // TAS semantics: `false` (bit=0) means "new action STARTS HERE" — it is a
      // point marker, not a duration. Setting every bit in a coarser cell to false
      // would create multiple 0.25s-spaced boundaries instead of one 1s boundary.
      // Fix: only the lead bit carries the intended new value; sub-bits within the
      // same visible cell must remain `true` (ongoing). When nextValue=true (removing
      // a boundary) or for non-TAS flows (where false means NQ quality over a
      // duration), all bits in range receive the same value unchanged.
      if (isTasFlow && k > 0 && nextValue === false) {
        newLabels[name] = true;
      } else {
        newLabels[name] = nextValue;
      }
    }
    set({ visited: newVisited, labels: newLabels });
    markDraftDirty();
    saveDraftSnapshot();
  },

  toggle: (idx: number) => {
    if (get().viewerReadOnly) return;
    const { images, visited, labels, viewerMode, actionLabelsLlmState, statsCache, myStatsCache } =
      get();
    const name = images[idx];
    if (!name) return;
    const toggleFeatures = myStatsCache?.features ?? statsCache?.features;

    // Block toggle if this frame is within a segment currently being analyzed by LLM.
    // Labels are keyed by bit index; LLM state is keyed by float seconds.
    if (isTasFlow && toggleFeatures?.llm_action_labels) {
      const bitIdx = parseInt(name, 10);
      const fps = Math.max(1, Math.floor(get().currentEpisodeAnnotationFps || 1));
      const timeSec = bitIdx / fps;
      for (const [startStr, state] of Object.entries(actionLabelsLlmState)) {
        if (state.status !== 'clipping' && state.status !== 'loading') continue;
        const startSec = parseFloat(startStr);
        const endSec = state.endSecond ?? Infinity;
        if (timeSec >= startSec && timeSec <= endSec) return;
      }
    }

    const newVisited = new Set(visited);
    newVisited.add(idx);

    const newLabels = { ...labels };
    const current = newLabels[name];
    if (isTasFlow && viewerMode === 'auditing') {
      // TAS audit: null → true (Ongoing=bit=1, default) → false (New Action=bit=0) → true
      // First click from null gives the default/positive state, same as secshot (!null = true)
      if (current === null) newLabels[name] = true;
      else if (current === true) newLabels[name] = false;
      else newLabels[name] = true;
    } else {
      newLabels[name] = !current;
    }

    set({ visited: newVisited, labels: newLabels });

    markDraftDirty();
    saveDraftSnapshot();

    // LLM state invalidation on boundary changes. Suppressed for hidden golden
    // so the operator's bit toggles don't touch production action_labels rows.
    // Also suppressed in audit mode: auditors review the annotator's existing
    // descriptions; boundary adjustments should not wipe them from the store.
    const isHiddenGoldenToggle = (get().leaseId ?? '').startsWith('g:');
    if (
      !isHiddenGoldenToggle &&
      viewerMode !== 'auditing' &&
      isTasFlow &&
      toggleFeatures?.llm_action_labels
    ) {
      const secondIdx = parseInt(name, 10);
      const newVal = newLabels[name];

      const fps = Math.max(1, Math.floor(get().currentEpisodeAnnotationFps || 1));

      // Returns the bit index of the nearest boundary before `beforeBit`.
      const findPrevBoundaryBit = (beforeBit: number): number => {
        for (let i = beforeBit - 1; i > 0; i--) {
          if (newLabels[String(i)] === false) return i;
        }
        return 0;
      };

      // actionLabels and actionLabelsLlmState are keyed by float seconds.
      const invalidateSegment = (startBit: number) => {
        const startSec = startBit / fps;
        set((s) => {
          const llm = { ...s.actionLabelsLlmState };
          const saved = { ...s.actionLabels };
          delete llm[startSec];
          delete saved[startSec];
          return { actionLabelsLlmState: llm, actionLabels: saved };
        });
      };

      if (newVal === false) {
        const prevStart = findPrevBoundaryBit(secondIdx);

        invalidateSegment(prevStart); // [prevStart, secondIdx) — range changed
        invalidateSegment(secondIdx); // [secondIdx, nextBoundary) — new segment, reset
      } else if (current === false) {
        const prevStart = findPrevBoundaryBit(secondIdx);

        invalidateSegment(secondIdx); // [secondIdx, next) no longer exists
        invalidateSegment(prevStart); // [prevStart, secondIdx) now extends to next boundary
      }
    }
  },

  toggleBatch: () => {
    if (get().viewerReadOnly) return;
    const { batchIndex, batchOffset, images, labels, visited } = get();
    const start = batchIndex * BATCH_SIZE + batchOffset;
    const end = Math.min(start + BATCH_SIZE, images.length);

    const newLabels = { ...labels };
    const newVisited = new Set(visited);
    for (let i = start; i < end; i += 1) {
      const name = images[i];
      if (!name) continue;
      newLabels[name] = !newLabels[name];
      newVisited.add(i);
    }
    set({ labels: newLabels, visited: newVisited });

    markDraftDirty();
    saveDraftSnapshot();
  },

  nextBatch: () => {
    // BATCH_SIZE visible cells per second-row.
    // Legacy mode:    shift by 4 SECONDS (= BATCH_SIZE * fps bits) so the
    //                 visible 4-cell window jumps to the next non-overlapping
    //                 batch in real time.
    // Continuous mode: shift by BATCH_SIZE bits (= 0.8s @ fps=5) — each cell
    //                 is 1 bit so a "batch" is the visible 0.8s window.
    const state = get();
    const fps = Math.max(1, Math.floor(state.currentEpisodeAnnotationFps || 1));
    const continuousPlayback = fps > 1;
    state.shiftWindowBy(continuousPlayback ? BATCH_SIZE : BATCH_SIZE * fps);
  },

  prevBatch: () => {
    const state = get();
    const fps = Math.max(1, Math.floor(state.currentEpisodeAnnotationFps || 1));
    const continuousPlayback = fps > 1;
    state.shiftWindowBy(continuousPlayback ? -BATCH_SIZE : -BATCH_SIZE * fps);
  },

  shiftWindowBy: (deltaSeconds: number) => {
    const { images, batchIndex, batchOffset, imageUrls } = get();
    if (images.length === 0) return;
    const maxStart = Math.max(0, images.length - 1);
    const currentStart = batchIndex * BATCH_SIZE + batchOffset;
    const nextStart = Math.max(0, Math.min(maxStart, currentStart + deltaSeconds));
    const nextBatchIndex = Math.floor(nextStart / BATCH_SIZE);
    const nextBatchOffset = nextStart - nextBatchIndex * BATCH_SIZE;

    set({ batchIndex: nextBatchIndex, batchOffset: nextBatchOffset });

    // Navigation-only — flag the draft as dirty so the 5-second autosave
    // timer (drafts.ts:248-259) persists the visited updates triggered by
    // the windowStart change. Don't call saveDraftSnapshot synchronously
    // here: it's a heavy JSON.stringify + localStorage write, and the
    // continuous-playback path calls shiftWindowBy ~5×/sec.
    // Label-changing actions (toggle / toggleRange / toggleBatch) still
    // persist immediately at their own call sites.
    markDraftDirty();

    if (!frameExtractor || totalPrefetchSeconds <= 0) return;
    const extractorForWindow = frameExtractor;
    const loadId = activeEpisodeLoadId;
    const targetEnd = Math.min(totalPrefetchSeconds, nextStart + BATCH_SIZE);
    const prefetch = scheduleBalancedFramePrefetch(nextStart, imageUrls);
    if (prefetch.blocked) {
      set({ imagesLoading: true, loadingProgress: activeDownloadProgress });
      return;
    }
    const currentPreviewUrl = get().previewVideoUrl;
    if (targetEnd > nextStart && !previewUrlCovers(currentPreviewUrl, targetEnd, loadId)) {
      const nextPreviewUrl = createPreviewUrlFor(targetEnd, loadId);
      if (nextPreviewUrl) {
        set({ previewVideoUrl: nextPreviewUrl });
        releasePreviewVideoUrl(currentPreviewUrl);
      }
    }

    if (prefetch.visibleSeconds.length > 0) {
      set({
        imagesLoading: true,
        loadingProgress: { text: 'Preparing preview frames...', percent: 0 },
      });
      void Promise.resolve(extractorForWindow.waitForSeconds(prefetch.visibleSeconds)).then(() => {
        const state = get();
        if (
          activeEpisodeLoadId === loadId &&
          state.batchIndex === nextBatchIndex &&
          state.batchOffset === nextBatchOffset
        ) {
          set({ imagesLoading: false });
        }
      });
    } else {
      set({ imagesLoading: false });
    }
  },

  startOperatorRandom: async (name: string) => {
    const { authUser } = get();
    if (authUser?.user === 'max' && name === 'max') {
      showToast('Please switch to another user to start annotating.');
      return;
    }

    // Set continuous to false for "Random" mode (fetch 1 and return)
    set({ mode: 'operator-annotate', continuous: false });
    get().showSection('viewer');

    let resp: AnnotateNextResponse | null = null;
    try {
      resp = await fetchJSON<AnnotateNextResponse>(apiPath('/annotate/next'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operator: name }),
      });
    } catch {
      // ignore
    }

    if (!resp?.episode?.id) {
      get().resetAnnotationSession();
      set({ leaseId: null });
      showToast('All caught up! No more episodes to annotate.');
      get().showOperatorDashboard();
      return;
    }

    // secpose is not a bitmask flow: skip the secshot-specific 2-vote gate and
    // the bitmask openEpisode load. SecPoseViewer (rendered at mode==='viewer')
    // reads selectedEpisode + leaseId and loads /secpose-assets itself.
    if (FLOW === 'secpose') {
      set({
        leaseId: resp.lease_id || null,
        selectedEpisode: resp.episode.id,
        viewerMode: 'annotating',
        rejectionMessage: null,
      });
      return;
    }

    const ep = resp.episode;
    const isCompleted = ep.mark_status === 'completed' || ep.mark_status === 'audited';
    if (isCompleted) {
      get().resetAnnotationSession();
      set({ leaseId: null });
      showToast('This episode is already complete. Please click Random again.');
      get().showOperatorDashboard();
      return;
    }

    set({ leaseId: resp.lease_id || null });
    const deviceTypeEnum = resolveDeviceTypeEnum(resp.episode);
    await get().openEpisode(resp.episode.id, deviceTypeEnum, { episode: resp.episode });
  },

  startAuditRandom: async (name: string) => {
    const { isAuditor, isAdmin } = get();
    if (!isAuditor && !isAdmin) {
      showToast('You are not an auditor.');
      return;
    }

    set({ mode: 'audit', continuous: false });
    get().showSection('viewer');

    try {
      const resp = await fetchJSON<AnnotateNextResponse>(apiPath('/audit/next'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operator: name }),
      });

      if (!resp?.episode?.id) {
        get().resetAnnotationSession();
        set({ leaseId: null });
        showToast('No pending audits available.');
        get().showOperatorDashboard();
        return;
      }

      // secpose: skip bitmask openEpisode; SecPoseViewer loads /secpose-assets.
      if (FLOW === 'secpose') {
        set({
          leaseId: resp.lease_id || null,
          selectedEpisode: resp.episode.id,
          viewerMode: 'auditing',
          rejectionMessage: null,
        });
        return;
      }

      set({ leaseId: resp.lease_id || null });

      const deviceTypeEnum = resolveDeviceTypeEnum(resp.episode);
      await get().openEpisode(resp.episode.id, deviceTypeEnum, { episode: resp.episode });
    } catch (err) {
      get().resetAnnotationSession();
      set({ leaseId: null });
      let message = '领取审核任务失败，请重试';
      if (err instanceof HttpError) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed && typeof parsed.detail === 'string') message = parsed.detail;
        } catch {
          /* ignore parse error */
        }
      }
      showToast(message);
      get().showOperatorDashboard();
      return;
    }
  },

  releaseActiveLease: async (kind: 'annotate' | 'audit') => {
    const { operator } = get();
    if (!operator) {
      showToast('Choose an operator first.');
      return;
    }
    // Hidden-golden assignments must never be releasable. Releasing would
    // quarantine the (reused production) episode id into released_episodes,
    // which silently kills injection for the whole golden set. We also must
    // not let the operator realize this is a golden task — so the release is a
    // completely silent no-op: no request, no toast, no state change. Detected
    // purely client-side from the `g:` token prefix (same check used on the
    // commit/toggle paths), so nothing on the wire reveals it.
    if (kind === 'annotate' && (get().leaseId ?? '').startsWith('g:')) {
      return;
    }
    try {
      const resp = await fetchJSON<{ ok: boolean; released: string | null }>(
        apiPath('/lease/release'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operator, kind }),
        }
      );
      const label = kind === 'audit' ? 'audit' : 'annotation';
      if (resp?.released) {
        showToast(`Released your ${label} lease. You can now load a new episode.`);
      } else {
        showToast(`No active ${label} lease to release.`);
      }
      // Reflect the drop locally and refresh dashboard counts/history.
      set({ leaseId: null });
      await Promise.all([
        get().fetchMyStatsData(true),
        get().fetchStatsData(true),
        get().loadOperatorHistory(),
      ]);
    } catch (err) {
      let message = 'Failed to release lease. Please try again.';
      if (err instanceof HttpError) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed && typeof parsed.detail === 'string') message = parsed.detail;
        } catch {
          /* ignore parse error */
        }
      }
      showToast(message);
    }
  },

  resumeDraftIfAvailable: async () => {
    const { episodeSpecifyFromUrl, operator } = get();
    if (episodeSpecifyFromUrl) return false;
    const draft = loadDraftForOperator(operator);
    if (!draft) return false;
    if (draft.markStatus && draft.markStatus === 'completed') {
      clearDraftForOperator(operator, draft.episodeId);
      return false;
    }

    setPendingRestoredStartSecond(draft.batchIndex * 4 + draft.batchOffset);
    set({
      leaseId: draft.leaseId ?? null,
      selectedDeviceTypeEnum: draft.deviceTypeEnum ?? null,
      continuous: false,
    });

    await get().openEpisode(draft.episodeId, draft.deviceTypeEnum ?? null);
    // updateDraftStatus(); // no-op
    return true;
  },
}));
